/**
 * The main arc (ten missions) and the side gigs (one hundred), as data the
 * mission runtime steps. Objectives are typed; positions are explicit or a
 * wake node's label resolved against the level. Variants keyed on testimony
 * change layouts: extra sensor nodes, extra patrols, a spared district.
 */
import type { HandlerId } from "./factions";
import type { Gate } from "./testimony";

/**
 * Where an objective sends you: metres, a wake node by label, or a place found from the nodes (Stage 692).
 *
 * `past` walks from the plaza's node A through the named node and on, `times` the A→node distance in
 * all (2: as far again, which from a corner intersection is the centre of the diagonal block beyond it),
 * then `dx`/`dz` metres. The district generator puts the four outer nodes on the plaza's corner
 * intersections in a district of any size, so a spot written this way is the same place on the same
 * block whether the district is three blocks across or five; metres typed against one size are not.
 */
export type Spot = { x: number; z: number } | { node: string } | { past: string; times: number; dx: number; dz: number };

/**
 * Who an escort is walking (Stage 677). Until this every escort was Ida Vessel: the three wake-cell
 * rescues walked a capsule tagged IDA VESSEL home, and the feed said IDA IS MOVING.
 */
export type EscortWho = "vessel" | "cell";
/** how the feed names them when they move or stop */
export const ESCORT_NAME: Record<EscortWho, string> = { vessel: "IDA", cell: "THE CELL" };

export type Objective =
  | { kind: "dialogue"; script: string; text: string }
  | { kind: "reach"; at: Spot; radius: number; text: string }
  | { kind: "kill"; target: "wasp" | "mech" | "dummy" | "any"; count: number; text: string }
  | { kind: "destroy"; text: string; spots: Spot[]; label: string }
  | { kind: "survive"; seconds: number; at?: Spot; radius?: number; text: string; waves?: number }
  | { kind: "escort"; path: Spot[]; speed: number; text: string; leash: number; who: EscortWho }
  | { kind: "hold"; at: Spot; radius: number; seconds: number; text: string; waves?: number };

export interface Reward {
  scrip?: number;
  xp?: number;
  protocol?: string;
  weapon?: "directive" | "clockeater" | "neon_edge";
  stamp?: string;
}

export interface Variant {
  gate: Gate;
  /** objectives replaced wholesale when the gate opens (the last open variant's win) */
  objectives?: Objective[];
  /**
   * objectives placed before whichever list wins (Stage 676). These compose: a variant that only
   * prepends never overwrites another variant's objectives, so two choices can both change a mission
   */
  prepend?: Objective[];
  extraWasps?: number;
  extraMechs?: number;
}

/** every objective a variant can put in front of the player: what it prepends, then what it replaces */
export function variantObjectives(v: Variant): Objective[] {
  return [...(v.prepend ?? []), ...(v.objectives ?? [])];
}

export interface MissionDef {
  id: string;
  kind: "mission" | "gig";
  /** arc order for missions; 0 for gigs */
  order: number;
  title: string;
  level: string;
  fixer: HandlerId;
  brief: string;
  objectives: Objective[];
  variants?: Variant[];
  reward: Reward;
  /** gigs: needed Threat Rating and testimony */
  requires?: { threat?: number; gate?: Gate; after?: string };
  /** VANTAGE presence on top of the district's own (before Threat) */
  wasps: number;
  mechs: number;
  /** cleared by killing the whole lattice / surviving; the mission fails when every Blank is down for this long */
  failAfterDownSeconds?: number;
  /** no Threat patrols on top (the white office has no guards) */
  noThreat?: boolean;
}

const D = (script: string, text: string): Objective => ({ kind: "dialogue", script, text });
const reach = (at: Spot, text: string, radius = 3): Objective => ({ kind: "reach", at, radius, text });

export const MISSIONS: readonly MissionDef[] = [
  {
    id: "m1_wake_unlisted",
    kind: "mission",
    order: 1,
    title: "WAKE UNLISTED",
    level: "lease_row",
    fixer: "deacon",
    brief: "STEAL YOUR OWN LEASE FILE FROM THE ESCROW TERMINAL AT THE B INTERSECTION. FIND OUT WHY YOU WERE FLAGGED.",
    objectives: [D("m1_intro", "READ THE STREET"), reach({ node: "B" }, "REACH THE ESCROW TERMINAL AT B"), { kind: "survive", seconds: 20, at: { node: "B" }, radius: 6, text: "HOLD THE TERMINAL WHILE THE FILE DECRYPTS", waves: 1 }, reach({ node: "E" }, "TAKE THE FILE FROM THE CABINET AT E"), D("m1_file", "THE FILE"), reach({ node: "A" }, "GET OUT THROUGH THE PLAZA")],
    reward: { scrip: 300, xp: 900, stamp: "mission:first" },
    wasps: 2,
    mechs: 0,
  },
  {
    id: "m2_deadletter_run",
    kind: "mission",
    order: 2,
    title: "DEADLETTER RUN",
    level: "deadletter_docks",
    fixer: "deacon",
    brief: "WORK THE DOCKS. CLEAR THE DRONE PATROLS OFF THE WAKE CELL'S ROUTES AND FIND THE INFORMANT AT C.",
    objectives: [{ kind: "kill", target: "wasp", count: 4, text: "DOWN 4 WASPS ON THE DOCK ROUTES" }, reach({ node: "C" }, "FIND THE INFORMANT AT C"), D("m2_informant", "THE INFORMANT"), { kind: "destroy", spots: [{ node: "D" }, { node: "B" }], label: "RELAY", text: "BREAK THE TWO VANTAGE RELAYS" }],
    // the cost of keeping the lease file in m1: the model knows a copy walked out of Lease Row and
    // it is looking for whoever is carrying it. Burning it is the quiet run.
    variants: [{ gate: { all: { "m1:lease": "keep" } }, extraWasps: 2 }],
    reward: { scrip: 400, xp: 1100, protocol: "red_lease" },
    wasps: 4,
    mechs: 0,
  },
  {
    id: "m3_repo_volatility",
    kind: "mission",
    order: 3,
    title: "VARIANCE",
    level: "repo_depot",
    fixer: "marrow",
    brief: "PULL THE DEPOT'S LOGS. HOLD THE PLAZA WHILE THEY COPY, AND DISABLE THE MECH VANTAGE SENDS TO STOP YOU.",
    objectives: [{ kind: "hold", at: { node: "A" }, radius: 7, seconds: 30, text: "HOLD THE PLAZA WHILE THE LOGS COPY", waves: 2 }, { kind: "kill", target: "mech", count: 1, text: "DISABLE THE REPO MECH" }, D("m3_volatility", "THE LOGS")],
    // Marrow's debt (Stage 671). Hand the docks informant to the Clockeaters in mission 2 and this is
    // their job: the Clockeaters settle their accounts, and they leave the depot's switchgear open.
    // Cut the yard lights first and the logs copy in the dark — a walk before the fight, and half the
    // hold. The spared informant pays you back on his own docks in mission 4; this is the other
    // choice's payment, one mission sooner. Before this stage mission 3 was the same for every file.
    variants: [
      {
        gate: { all: { "m2:informant": "turn" } },
        objectives: [
          reach({ node: "D" }, "MARROW'S PEOPLE LEFT THE DEPOT SWITCHGEAR OPEN AT D. CUT THE YARD LIGHTS"),
          { kind: "hold", at: { node: "A" }, radius: 7, seconds: 15, text: "HOLD THE PLAZA IN THE DARK WHILE THE LOGS COPY", waves: 1 },
          { kind: "kill", target: "mech", count: 1, text: "DISABLE THE REPO MECH" },
          D("m3_volatility", "THE LOGS"),
        ],
      },
    ],
    reward: { scrip: 500, xp: 1300, protocol: "filament_core" },
    wasps: 3,
    mechs: 1,
  },
  {
    id: "m4_the_leak",
    kind: "mission",
    order: 4,
    title: "THE LEAK",
    level: "deadletter_docks",
    fixer: "vessel",
    brief: "AN ESTATE DEFECTOR HANDS YOU THE DIRECTIVE. WALK HER FROM B TO D UNDER A VANTAGE SWEEP WHILE WERN ARGUES HIS CASE.",
    objectives: [D("m4_leak", "THE DIRECTIVE"), { kind: "escort", path: [{ node: "B" }, { node: "A" }, { node: "D" }], speed: 2.2, leash: 8, who: "vessel", text: "WALK IDA VESSEL FROM B TO D" }, { kind: "kill", target: "wasp", count: 3, text: "CLEAR THE SWEEP" }],
    // The docks informant's two endings, played out on his own docks (Stage 663). Before this the
    // choice in mission 2 was read by nothing in the arc — one optional gig — so mercy and betrayal
    // produced the same mission. Now they produce different ones: spared, he repays it with the
    // sweep's ears; handed over, the routes he had already sold are still live.
    variants: [
      {
        gate: { all: { "m2:informant": "spare" } },
        objectives: [
          D("m4_leak", "THE DIRECTIVE"),
          { kind: "destroy", spots: [{ node: "C" }], label: "VANTAGE SPEAKER", text: "THE INFORMANT LEFT HIS SPEAKER AT C. KILL IT AND THE SWEEP GOES DEAF" },
          { kind: "escort", path: [{ node: "B" }, { node: "A" }, { node: "D" }], speed: 2.2, leash: 8, who: "vessel", text: "WALK IDA VESSEL FROM B TO D WHILE THE SWEEP IS DEAF" },
          { kind: "kill", target: "wasp", count: 1, text: "CLEAR WHAT IS LEFT OF THE SWEEP" },
        ],
      },
      {
        gate: { all: { "m2:informant": "turn" } },
        extraWasps: 2,
        objectives: [
          D("m4_leak", "THE DIRECTIVE"),
          { kind: "escort", path: [{ node: "B" }, { node: "A" }, { node: "D" }], speed: 2.2, leash: 8, who: "vessel", text: "WALK IDA VESSEL FROM B TO D" },
          { kind: "kill", target: "wasp", count: 5, text: "CLEAR THE SWEEP — IT IS RUNNING THE ROUTES HE SOLD" },
        ],
      },
    ],
    reward: { scrip: 600, xp: 1600, weapon: "directive" },
    wasps: 4,
    mechs: 1,
  },
  {
    id: "m5_blind_the_model",
    kind: "mission",
    order: 5,
    title: "BLIND THE MODEL",
    level: "lease_row",
    fixer: "deacon",
    brief: "DESTROY THE SENSOR LATTICE DISTRICT BY DISTRICT. VANTAGE RESPONDS LIKE AN IMMUNE SYSTEM — THE HARDEST COMBAT IN THE ARC.",
    // The four inner nodes are the district generator's own, which are always on open ground. The
    // two outer posts were written as (0, ±30) and both landed inside a 4.2 m building on LEASE
    // ROW, sealing a 1.8 m lattice node in concrete where nothing could shoot it (Stage 175).
    // These two were found by scanning the level rather than typed: 4.4 m of clearance each — the
    // generator gives its own nodes 4.2 — on opposite outer diagonals, so the six spread across
    // the district the brief says to blind. They were (32, -32) and (-34, 34): the courtyards of the
    // blocks diagonally beyond nodes D and C. Since Stage 692 they are written from those nodes, so
    // they stay in those courtyards when LEASE ROW is five blocks across instead of three.
    objectives: [D("m5_lattice", "THE LATTICE"), { kind: "destroy", spots: [{ node: "B" }, { node: "C" }, { node: "D" }, { node: "E" }, { past: "D", times: 2, dx: -1, dz: 1 }, { past: "C", times: 2, dx: -1, dz: 1 }], label: "LATTICE NODE", text: "PUT OUT THE SIX LATTICE NODES" }, { kind: "survive", seconds: 40, at: { node: "A" }, radius: 12, text: "SURVIVE THE IMMUNE RESPONSE AT THE PLAZA", waves: 3 }],
    variants: [
      { gate: { all: { "m3:volatility": "publish" } }, objectives: [D("m5_lattice", "THE LATTICE"), { kind: "destroy", spots: [{ node: "B" }, { node: "C" }, { node: "D" }, { node: "E" }], label: "LATTICE NODE", text: "PUT OUT THE FOUR LATTICE NODES (THE FEEDS ALREADY TOOK TWO)" }, { kind: "survive", seconds: 40, at: { node: "A" }, radius: 12, text: "SURVIVE THE IMMUNE RESPONSE AT THE PLAZA", waves: 3 }] },
      // The Directive in mission 4. Until this, keeping it or giving it to Ida changed
      // nothing you played: the endings read it and a file note mentioned it. Kept, it is the one
      // copy outside the Estate, and the Estate wants it back — it writes a repo writ and VANTAGE
      // sends a mech to serve it before the lattice can be touched. Given, the Estate is reading its
      // own hand and pulls its audit drones off the row it would have been hunting you on. Both carry
      // no objectives of their own to replace, so they compose with the publish variant above.
      { gate: { all: { "m4:directive": "kept" } }, extraMechs: 1, prepend: [{ kind: "kill", target: "mech", count: 1, text: "THE ESTATE WROTE A REPO WRIT FOR THE DIRECTIVE. PUT DOWN THE MECH SERVING IT" }] },
      { gate: { all: { "m4:directive": "given" } }, extraWasps: -2 },
    ],
    reward: { scrip: 800, xp: 2000, protocol: "wern_pulse" },
    wasps: 6,
    mechs: 1,
  },
  {
    id: "m6_trial_by_data",
    kind: "mission",
    order: 6,
    title: "TRIAL BY DATA",
    level: "repo_depot",
    fixer: "deacon",
    brief: "HOLD THE BROADCAST TOWER ON THE PLAZA WHILE THE DIRECTIVE GOES OUT ON EVERY LEASED FEED AND THE CITY WAKES LIVE AROUND YOU.",
    objectives: [{ kind: "hold", at: { node: "A" }, radius: 8, seconds: 45, text: "HOLD THE TOWER — THE DIRECTIVE IS BROADCASTING", waves: 3 }, D("m6_broadcast", "THE UPLINK"), { kind: "hold", at: { node: "A" }, radius: 8, seconds: 30, text: "HOLD UNTIL THE UPLINK CLOSES", waves: 2 }],
    // "Evidence is a weapon" — m1's kept lease file is the proof the broadcast can attach, and a city
    // that is shown the paper believes faster than one that is only told. Half the closing hold.
    variants: [
      { gate: { all: { "m1:lease": "keep" } }, objectives: [{ kind: "hold", at: { node: "A" }, radius: 8, seconds: 45, text: "HOLD THE TOWER — THE DIRECTIVE IS BROADCASTING", waves: 3 }, D("m6_broadcast", "THE UPLINK"), { kind: "hold", at: { node: "A" }, radius: 8, seconds: 15, text: "HOLD UNTIL THE UPLINK CLOSES — THE LEASE FILE IS GOING OUT WITH IT", waves: 1 }] },
      // Blinding the whole lattice in mission 5 left the model with nothing to route a response
      // by, so the tower draws two fewer drones (Stage 663). Wasps only, never objectives, so this
      // composes with the lease variant above instead of overwriting it; Wern names the cost of the
      // same blindness in the white office.
      { gate: { all: { "m5:lattice": "all" } }, extraWasps: -2 },
      // Sparing the docks in mission 5 left the model one eye, and it is looking at the depot: the
      // harbour lattice relays the tower to VANTAGE, and it has to be cut before the broadcast can go
      // out (Stage 676). Prepended, so it composes with the lease variant's closing hold.
      { gate: { all: { "m5:lattice": "spare_docks" } }, prepend: [{ kind: "destroy", spots: [{ node: "E" }], label: "HARBOUR RELAY", text: "YOU LEFT THE DOCKS THEIR EYE. CUT ITS RELAY AT E BEFORE IT CALLS THE TOWER IN" }] },
    ],
    reward: { scrip: 1000, xp: 2400, protocol: "blood_ledger", stamp: "mission:trial" },
    wasps: 6,
    mechs: 2,
  },
  {
    id: "m7_white_office",
    kind: "mission",
    order: 7,
    title: "THE WHITE OFFICE",
    level: "white_office",
    fixer: "wern",
    brief: "WERN DOESN'T FIGHT. HE OFFERS YOU THE LEASE SYSTEM ITSELF. THE FINAL INPUT IS A CHOICE.",
    objectives: [reach({ x: 0, z: -4 }, "APPROACH THE DESK", 2.5), D("m7_office", "THE OFFER")],
    reward: { scrip: 0, xp: 3000, protocol: "directive_optic", stamp: "arc:complete" },
    wasps: 0,
    mechs: 0,
    noThreat: true,
  },
  {
    id: "m8_night_ledger",
    kind: "mission",
    order: 8,
    title: "THE NIGHT LEDGER",
    level: "night_market",
    fixer: "deacon",
    brief: "THE WHITE OFFICE IS BEHIND YOU. THE MARKET STILL SELLS THE NAMES. WALK THE STALLS AND CLEAR THE SWEEP.",
    objectives: [D("m8_market", "THE STALLS"), reach({ node: "B" }, "REACH THE NAME STALL AT B"), { kind: "kill", target: "wasp", count: 3, text: "CLEAR THE MARKET SWEEP" }],
    variants: [
      {
        gate: { all: { "m7:ending": "wipe" } },
        extraWasps: 2,
        objectives: [D("m8_market", "THE STALLS"), reach({ node: "B" }, "REACH THE NAME STALL AT B"), { kind: "kill", target: "wasp", count: 5, text: "CLEAR THE SWEEP — THE WIPE LEFT THE MARKET HUNGRY" }],
      },
      { gate: { all: { "m7:ending": "chair" } }, prepend: [reach({ node: "C" }, "THE STALL THAT KEPT THE PAPER IS AT C")] },
    ],
    reward: { scrip: 500, xp: 1200 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "m9_high_wire",
    kind: "mission",
    order: 9,
    title: "THE HIGH WIRE",
    level: "relay_heights",
    fixer: "marrow",
    brief: "THE RELAY STILL CARRIES THE CHAIR'S SIGNAL. HOLD THE PLAZA WHILE MARROW CUTS THE WIRE.",
    objectives: [reach({ node: "C" }, "REACH THE RELAY POST AT C"), { kind: "hold", at: { node: "A" }, radius: 7, seconds: 20, text: "HOLD THE PLAZA WHILE THE WIRE IS CUT", waves: 1 }, D("m9_wire", "THE WIRE")],
    variants: [
      { gate: { all: { "m1:lease": "keep" } }, prepend: [{ kind: "destroy", spots: [{ node: "E" }], label: "LEASE RELAY", text: "THE KEPT LEASE FILE IS STILL ON THE WIRE. CUT THE RELAY AT E" }] },
    ],
    reward: { scrip: 600, xp: 1400 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "m10_glass_mile",
    kind: "mission",
    order: 10,
    title: "THE GLASS MILE",
    level: "glass_mile",
    fixer: "wern",
    brief: "WERN DOES NOT COME TO THE OFFICE. THE LAST CALL IS ON THE MILE: WALK THE GLASS AND PUT THE SWEEP DOWN.",
    objectives: [D("m10_glass", "THE MILE"), reach({ node: "D" }, "WALK THE GLASS TO D"), { kind: "kill", target: "wasp", count: 2, text: "PUT THE MILE SWEEP DOWN" }],
    variants: [{ gate: { all: { "m4:directive": "kept" } }, extraMechs: 1, prepend: [{ kind: "kill", target: "mech", count: 1, text: "THE ESTATE SENT A MECH FOR THE DIRECTIVE YOU KEPT" }] }],
    reward: { scrip: 700, xp: 1600 },
    wasps: 2,
    mechs: 0,
  },
  // ---- gigs ----
  { id: "g_escrow_row", kind: "gig", order: 0, title: "ESCROW HEIST · LEASE ROW", level: "lease_row", fixer: "marrow", brief: "CRACK THE ESCROW TERMINAL AT D AND GET THE SLEEP CREDIT OUT BEFORE THE PATROL TURNS.", objectives: [reach({ node: "D" }, "CRACK THE ESCROW AT D"), { kind: "survive", seconds: 15, at: { node: "D" }, radius: 6, text: "HOLD WHILE IT DUMPS", waves: 1 }, reach({ node: "A" }, "OUT THROUGH THE PLAZA")], reward: { scrip: 250, xp: 500, stamp: "gig:first" }, wasps: 2, mechs: 0 },
  { id: "g_convoy_docks", kind: "gig", order: 0, title: "DRONE CONVOY · DOCKS", level: "deadletter_docks", fixer: "deacon", brief: "A WASP CONVOY CROSSES THE DOCKS AT HEIGHT. AMBUSH IT FROM THE WALKWAY.", objectives: [reach({ node: "B" }, "TAKE THE WALKWAY OVER B"), { kind: "kill", target: "wasp", count: 3, text: "DOWN THE CONVOY" }], reward: { scrip: 300, xp: 600 }, wasps: 3, mechs: 0 },
  { id: "g_rescue_depot", kind: "gig", order: 0, title: "WAKE-CELL RESCUE · DEPOT", level: "repo_depot", fixer: "deacon", brief: "A CELL IS PINNED UNDER THE IMPOUND SEARCHLIGHT AT C. GET THEM OUT.", objectives: [reach({ node: "C" }, "REACH THE PINNED CELL AT C"), { kind: "escort", path: [{ node: "C" }, { node: "A" }], speed: 2.4, leash: 8, who: "cell", text: "WALK THEM TO THE PLAZA" }], reward: { scrip: 350, xp: 700, protocol: "red_lease" }, wasps: 3, mechs: 1, requires: { threat: 1 } },
  { id: "g_lattice_row", kind: "gig", order: 0, title: "SENSOR SABOTAGE · LEASE ROW", level: "lease_row", fixer: "vessel", brief: "TWO LATTICE POSTS ON THE WALKWAY STREET. THE ESTATE WANTS THEM DARK BEFORE THE AUDIT.", objectives: [{ kind: "destroy", spots: [{ node: "B" }, { node: "C" }], label: "SENSOR POST", text: "BREAK THE TWO SENSOR POSTS" }], reward: { scrip: 300, xp: 600 }, wasps: 2, mechs: 0, requires: { gate: { not: { "m4:vessel": "expose" } } } },
  { id: "g_escrow_depot", kind: "gig", order: 0, title: "ESCROW HEIST · DEPOT", level: "repo_depot", fixer: "marrow", brief: "THE IMPOUND LOT KEEPS A SECOND ESCROW. TAKE IT WHILE THE MECH IS AT THE FAR FENCE.", objectives: [reach({ node: "E" }, "REACH THE LOT ESCROW AT E"), { kind: "survive", seconds: 20, at: { node: "E" }, radius: 6, text: "HOLD THE DUMP", waves: 1 }, reach({ node: "B" }, "OUT THROUGH B")], reward: { scrip: 400, xp: 800, weapon: "clockeater" }, wasps: 3, mechs: 1, requires: { threat: 2 } },
  { id: "g_convoy_row", kind: "gig", order: 0, title: "DRONE CONVOY · LEASE ROW", level: "lease_row", fixer: "marrow", brief: "FOUR WASPS RUN THE PLAZA LOOP EVERY NIGHT. BREAK THE LOOP.", objectives: [{ kind: "kill", target: "wasp", count: 4, text: "BREAK THE LOOP" }], reward: { scrip: 350, xp: 700 }, wasps: 4, mechs: 0, requires: { threat: 2 } },
  { id: "g_rescue_docks", kind: "gig", order: 0, title: "WAKE-CELL RESCUE · DOCKS", level: "deadletter_docks", fixer: "deacon", brief: "A CELL WENT DARK AT E. BRING WHOEVER IS LEFT TO THE PLAZA.", objectives: [reach({ node: "E" }, "REACH E"), { kind: "escort", path: [{ node: "E" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM HOME" }, { kind: "kill", target: "wasp", count: 2, text: "COVER THE WALK" }], reward: { scrip: 400, xp: 800, protocol: "blood_ledger" }, wasps: 4, mechs: 0, requires: { threat: 3 }, variants: [{ gate: { all: { "m5:lattice": "spare_docks" } }, extraWasps: 2 }] },
  { id: "g_lattice_docks", kind: "gig", order: 0, title: "SENSOR SABOTAGE · DOCKS", level: "deadletter_docks", fixer: "vessel", brief: "THREE LATTICE POSTS ALONG THE CRANE LINE.", objectives: [{ kind: "destroy", spots: [{ node: "B" }, { node: "D" }, { node: "E" }], label: "SENSOR POST", text: "BREAK THE THREE POSTS" }], reward: { scrip: 450, xp: 900, protocol: "filament_core" }, wasps: 3, mechs: 1, requires: { threat: 3, gate: { not: { "m4:vessel": "expose", "m5:lattice": "all" } } } },
  { id: "g_escrow_docks", kind: "gig", order: 0, title: "ESCROW HEIST · DOCKS", level: "deadletter_docks", fixer: "marrow", brief: "THE HARBOUR ESCROW AT C PAYS IN CLOCKEATER TIME.", objectives: [reach({ node: "C" }, "CRACK THE HARBOUR ESCROW"), { kind: "survive", seconds: 25, at: { node: "C" }, radius: 6, text: "HOLD THE DUMP", waves: 2 }], reward: { scrip: 500, xp: 1000 }, wasps: 4, mechs: 1, requires: { threat: 4, gate: { not: { "m2:informant": "turn" } } } },
  { id: "g_convoy_depot", kind: "gig", order: 0, title: "DRONE CONVOY · DEPOT", level: "repo_depot", fixer: "deacon", brief: "THE DEPOT CONVOY FLIES WITH A MECH ESCORT.", objectives: [{ kind: "kill", target: "wasp", count: 4, text: "DOWN THE CONVOY" }, { kind: "kill", target: "mech", count: 1, text: "DISABLE THE ESCORT" }], reward: { scrip: 600, xp: 1200, protocol: "wern_pulse" }, wasps: 4, mechs: 1, requires: { threat: 5 } },
  { id: "g_rescue_row", kind: "gig", order: 0, title: "WAKE-CELL RESCUE · LEASE ROW", level: "lease_row", fixer: "deacon", brief: "THE LAST CELL ON THE ROW IS PINNED AT D WITH A MECH ON THEM.", objectives: [reach({ node: "D" }, "REACH D"), { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH" }, { kind: "escort", path: [{ node: "D" }, { node: "A" }], speed: 2.4, leash: 8, who: "cell", text: "WALK THEM TO THE PLAZA" }], reward: { scrip: 700, xp: 1400, weapon: "neon_edge" }, wasps: 4, mechs: 1, requires: { threat: 0 } },
  { id: "g_lattice_depot", kind: "gig", order: 0, title: "SENSOR SABOTAGE · DEPOT", level: "repo_depot", fixer: "vessel", brief: "THE DEPOT LATTICE IS THE LAST ONE THE ESTATE AUDIT CAN SEE THROUGH.", objectives: [{ kind: "destroy", spots: [{ node: "B" }, { node: "C" }, { node: "D" }, { node: "E" }], label: "SENSOR POST", text: "BREAK ALL FOUR POSTS" }, { kind: "survive", seconds: 30, at: { node: "A" }, radius: 10, text: "SURVIVE THE RESPONSE", waves: 2 }], reward: { scrip: 800, xp: 1600, protocol: "directive_optic" }, wasps: 5, mechs: 2, requires: { threat: 7, gate: { not: { "m4:vessel": "expose" } } } },
  {
    id: "g_awning_cell",
    kind: "gig",
    order: 0,
    title: "AWNING EXTRACTION · NIGHT MARKET",
    level: "night_market",
    fixer: "deacon",
    brief: "A WAKE CELL IS HIDING UNDER THE AWNINGS AT C, AND THE STALLS WILL NOT COVER THEM PAST THE NEXT SWEEP. WALK THEM TO THE PLAZA BEFORE THE PATROL READS THE CROWD.",
    objectives: [
      reach({ node: "C" }, "FIND THE CELL UNDER THE AWNINGS AT C"),
      { kind: "escort", path: [{ node: "C" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM TO THE PLAZA" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE SWEEP OFF THE STALLS" },
    ],
    reward: { scrip: 320, xp: 640 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_uplink_skim",
    kind: "gig",
    order: 0,
    title: "UPLINK SKIM · RELAY HEIGHTS",
    level: "relay_heights",
    fixer: "marrow",
    brief: "THE UPLINK AT B IS LEASING CYCLES THE CLOCKEATERS NEVER SIGNED FOR. HOLD THE RACK WHILE THE DUMP RUNS, THEN CUT THE SENSOR ON THE FAR TOWER.",
    objectives: [
      reach({ node: "B" }, "REACH THE UPLINK RACK AT B"),
      { kind: "survive", seconds: 18, at: { node: "B" }, radius: 6, text: "HOLD THE RACK WHILE IT DUMPS", waves: 1 },
      { kind: "destroy", spots: [{ node: "E" }], label: "SENSOR POST", text: "CUT THE SENSOR ON THE FAR TOWER" },
      reach({ node: "A" }, "OUT THROUGH THE PLAZA"),
    ],
    reward: { scrip: 380, xp: 760 },
    wasps: 3,
    mechs: 1,
  },
  {
    id: "g_sluice_dark",
    kind: "gig",
    order: 0,
    title: "SLUICE DARK · ASH CANAL",
    level: "ash_canal",
    fixer: "vessel",
    brief: "THREE LATTICE POSTS WATCH THE SLUICE, AND THE ESTATE AUDIT IS DUE BEFORE DAWN. BREAK THEM AND HOLD THE LOCK UNTIL THE WATER GOES DARK.",
    objectives: [
      { kind: "destroy", spots: [{ node: "B" }, { node: "D" }, { node: "E" }], label: "SENSOR POST", text: "BREAK THE THREE SLUICE POSTS" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 20, text: "HOLD THE LOCK WHILE THE WATER GOES DARK", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE AUDIT DRONES" },
    ],
    reward: { scrip: 420, xp: 840 },
    wasps: 2,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_showroom_heat",
    kind: "gig",
    order: 0,
    title: "SHOWROOM HEAT · GLASS MILE",
    level: "glass_mile",
    fixer: "deacon",
    brief: "A CELL TOOK THE SHOWROOM STAIRS AT E AND THE GLASS WILL NOT HIDE A HEAT SIGNATURE. BRING THEM DOWN TO THE PLAZA, AND CLEAR THE WASPS THAT FOLLOW THE REFLECTION.",
    objectives: [
      reach({ node: "E" }, "REACH THE CELL ON THE SHOWROOM STAIRS AT E"),
      { kind: "escort", path: [{ node: "E" }, { node: "A" }], speed: 2.3, leash: 8, who: "cell", text: "WALK THEM DOWN TO THE PLAZA" },
      { kind: "kill", target: "wasp", count: 3, text: "CLEAR THE WASPS ON THE REFLECTION" },
    ],
    reward: { scrip: 360, xp: 720 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_clinic_ledger",
    kind: "gig",
    order: 0,
    title: "CLINIC LEDGER · BONE MARKET",
    level: "bone_market",
    fixer: "marrow",
    brief: "THE BACK CLINIC AT D IS SELLING SLEEP CREDIT UNDER THE TABLE, AND MARROW WANTS THE LEDGER BEFORE IT CLOSES. CRACK THE TERMINAL, HOLD THE DUMP, AND WALK OUT THROUGH B.",
    objectives: [
      reach({ node: "D" }, "CRACK THE CLINIC TERMINAL AT D"),
      { kind: "survive", seconds: 16, at: { node: "D" }, radius: 6, text: "HOLD THE DUMP", waves: 1 },
      reach({ node: "B" }, "OUT THROUGH B"),
    ],
    reward: { scrip: 400, xp: 800 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_bonded_cold",
    kind: "gig",
    order: 0,
    title: "BONDED COLD · COLD VAULT",
    level: "cold_vault",
    fixer: "vessel",
    brief: "A COLD CONVOY IS MOVING BONDED CRATES THE ESTATE DID NOT DECLARE. DOWN THE WASPS AT THE LOCKERS, DISABLE THE MECH ON THE DOOR, AND BREAK THE TWO SENSOR POSTS THAT CALL IT IN.",
    objectives: [
      { kind: "kill", target: "wasp", count: 3, text: "DOWN THE WASPS AT THE LOCKERS" },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH ON THE DOOR" },
      { kind: "destroy", spots: [{ node: "B" }, { node: "C" }], label: "SENSOR POST", text: "BREAK THE TWO POSTS THAT CALL THE CONVOY IN" },
    ],
    reward: { scrip: 480, xp: 960 },
    wasps: 3,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_vespers",
    kind: "gig",
    order: 0,
    title: "VESPERS · NEON CHAPEL",
    level: "neon_chapel",
    fixer: "deacon",
    brief: "VESPERS AT THE NAVE IS A WAKE MEETING THE MODEL HAS ALREADY PRICED. HOLD THE PLAZA THROUGH THE SERVICE, THEN WALK THE CELL FROM C OUT BEFORE THE LAST LIGHT.",
    objectives: [
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 22, text: "HOLD THE NAVE THROUGH VESPERS", waves: 1 },
      reach({ node: "C" }, "FIND THE CELL AT C"),
      { kind: "escort", path: [{ node: "C" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OUT BEFORE THE LAST LIGHT" },
    ],
    reward: { scrip: 440, xp: 880 },
    wasps: 2,
    mechs: 0,
  },
  {
    id: "g_hot_pour",
    kind: "gig",
    order: 0,
    title: "HOT POUR · SLAG PIT",
    level: "slag_pit",
    fixer: "marrow",
    brief: "THE POUR AT E IS RUNNING HOT AND THE IMPOUND MECH IS READING THE SCALE. REACH THE PIT, SURVIVE THE SHIFT WHILE THE CLOCKEATERS SKIM THE LOG, AND GET OUT THROUGH THE PLAZA.",
    objectives: [
      reach({ node: "E" }, "REACH THE POUR AT E"),
      { kind: "survive", seconds: 20, at: { node: "E" }, radius: 6, text: "HOLD THE PIT WHILE THE LOG SKIMS", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH ON THE SCALE" },
      reach({ node: "A" }, "OUT THROUGH THE PLAZA"),
    ],
    reward: { scrip: 520, xp: 1040 },
    wasps: 2,
    mechs: 2,
  },
  {
    id: "g_live_cable",
    kind: "gig",
    order: 0,
    title: "LIVE CABLE · WIRE GARDEN",
    level: "wire_garden",
    fixer: "vessel",
    brief: "LIVE CABLE CROSSES THE GARDEN ON POSTS THE ESTATE STILL TRUSTS. CUT THE THREE TAPS, HOLD THE PLAZA WHILE THE GRID DROPS, AND CLEAR THE WASPS THAT COME TO REPAIR IT.",
    objectives: [
      { kind: "destroy", spots: [{ node: "B" }, { node: "C" }, { node: "D" }], label: "CABLE TAP", text: "CUT THE THREE TAPS" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE PLAZA WHILE THE GRID DROPS", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE REPAIR WASPS" },
    ],
    reward: { scrip: 460, xp: 920 },
    wasps: 3,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_dark_fiber",
    kind: "gig",
    order: 0,
    title: "DARK FIBER · BLACK RELAY",
    level: "black_relay",
    fixer: "marrow",
    brief: "DARK FIBER AT C IS CARRYING A LEASE THE CLOCKEATERS WANT ERASED BEFORE IT PINGS. CRACK THE DISH, HOLD WHILE THE SILENCE WRITES, AND WALK A CELL OUT FROM E.",
    objectives: [
      reach({ node: "C" }, "CRACK THE DISH AT C"),
      { kind: "survive", seconds: 18, at: { node: "C" }, radius: 6, text: "HOLD WHILE THE SILENCE WRITES", waves: 1 },
      reach({ node: "E" }, "REACH THE CELL AT E"),
      { kind: "escort", path: [{ node: "E" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THE CELL OUT THROUGH THE PLAZA" },
    ],
    reward: { scrip: 540, xp: 1080 },
    wasps: 4,
    mechs: 1,
  },
  {
    id: "g_kiln_bisque",
    kind: "gig",
    order: 0,
    title: "BISQUE SHIFT · RED KILN",
    level: "red_kiln",
    fixer: "deacon",
    brief: "A CELL IS PINNED ON THE FIRE LANE AT C, AND THE KILN WILL NOT COOL BEFORE THE NEXT SWEEP. WALK THEM TO THE PLAZA BEFORE THE HEAT SIGNATURE IS READ.",
    objectives: [
      reach({ node: "C" }, "FIND THE CELL ON THE FIRE LANE AT C"),
      { kind: "escort", path: [{ node: "C" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OFF THE KILN TO THE PLAZA" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE SWEEP OFF THE FIRE LANE" },
    ],
    reward: { scrip: 340, xp: 680 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_bale_cut",
    kind: "gig",
    order: 0,
    title: "BALE CUT · PAPER WHARF",
    level: "paper_wharf",
    fixer: "marrow",
    brief: "THE CUSTOMS RACK AT B IS STAMPING MANIFESTS THE CLOCKEATERS NEVER SIGNED. HOLD THE DUMP, THEN CUT THE SENSOR ON THE FAR PIER.",
    objectives: [
      reach({ node: "B" }, "REACH THE CUSTOMS RACK AT B"),
      { kind: "survive", seconds: 16, at: { node: "B" }, radius: 6, text: "HOLD THE RACK WHILE THE MANIFEST DUMPS", waves: 1 },
      { kind: "destroy", spots: [{ node: "E" }], label: "SENSOR POST", text: "CUT THE SENSOR ON THE FAR PIER" },
      reach({ node: "A" }, "OUT THROUGH THE PLAZA"),
    ],
    reward: { scrip: 390, xp: 780 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_member_door",
    kind: "gig",
    order: 0,
    title: "MEMBER DOOR · VELVET COURT",
    level: "velvet_court",
    fixer: "vessel",
    brief: "TWO LATTICE POSTS WATCH THE MEMBER DOOR, AND THE ESTATE AUDIT IS ALREADY IN THE COURT. BREAK THEM, HOLD THE PLAZA, AND CLEAR THE WASPS THAT READ THE COVER.",
    objectives: [
      { kind: "destroy", spots: [{ node: "B" }, { node: "D" }], label: "SENSOR POST", text: "BREAK THE TWO POSTS ON THE MEMBER DOOR" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE COURT WHILE THE DOOR GOES DARK", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS ON THE COVER" },
    ],
    reward: { scrip: 410, xp: 820 },
    wasps: 2,
    mechs: 0,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_flake_deck",
    kind: "gig",
    order: 0,
    title: "FLAKE DECK · RUST CROWN",
    level: "rust_crown",
    fixer: "deacon",
    brief: "A CELL TOOK THE CROWN DECK AT E AND THE OXIDE WILL NOT HIDE THEM PAST THE NEXT CLIMB. WALK THEM TO THE PLAZA AND CLEAR THE WASPS THAT FOLLOW THE FLAKE.",
    objectives: [
      reach({ node: "E" }, "REACH THE CELL ON THE CROWN DECK AT E"),
      { kind: "escort", path: [{ node: "E" }, { node: "A" }], speed: 2.3, leash: 8, who: "cell", text: "WALK THEM DOWN TO THE PLAZA" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS ON THE CLIMB" },
    ],
    reward: { scrip: 430, xp: 860 },
    wasps: 3,
    mechs: 1,
  },
  {
    id: "g_white_out",
    kind: "gig",
    order: 0,
    title: "WHITE OUT · SALT STAIRS",
    level: "salt_stairs",
    fixer: "marrow",
    brief: "THE LANDING AT D IS SELLING SLEEP CREDIT UNDER A WHITE OUT, AND MARROW WANTS IT BEFORE THE STAIRS CLEAR. CRACK THE TERMINAL, HOLD THE DUMP, AND LEAVE THROUGH B.",
    objectives: [
      reach({ node: "D" }, "CRACK THE LANDING TERMINAL AT D"),
      { kind: "survive", seconds: 16, at: { node: "D" }, radius: 6, text: "HOLD THE DUMP", waves: 1 },
      reach({ node: "B" }, "OUT THROUGH B"),
    ],
    reward: { scrip: 450, xp: 900 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_spare_shade",
    kind: "gig",
    order: 0,
    title: "SPARE SHADE · LAMP BAZAAR",
    level: "lamp_bazaar",
    fixer: "vessel",
    brief: "A SPARE-BULB CONVOY IS MOVING WATTAGE THE ESTATE DID NOT DECLARE. DOWN THE WASPS AT THE STALLS, DISABLE THE MECH ON THE SHADE, AND BREAK THE TWO POSTS THAT CALL IT IN.",
    objectives: [
      { kind: "kill", target: "wasp", count: 2, text: "DOWN THE WASPS AT THE STALLS" },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH ON THE SHADE" },
      { kind: "destroy", spots: [{ node: "B" }, { node: "C" }], label: "SENSOR POST", text: "BREAK THE TWO POSTS THAT CALL THE CONVOY IN" },
    ],
    reward: { scrip: 470, xp: 940 },
    wasps: 3,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_row_six",
    kind: "gig",
    order: 0,
    title: "ROW SIX · DEBT ORCHARD",
    level: "debt_orchard",
    fixer: "deacon",
    brief: "ROW SIX IS A WAKE MEETING THE MODEL HAS ALREADY PRICED. HOLD THE PLAZA THROUGH THE PICK, THEN WALK THE CELL FROM C OUT BEFORE THE BILL COMES DUE.",
    objectives: [
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 20, text: "HOLD THE ORCHARD THROUGH THE PICK", waves: 1 },
      reach({ node: "C" }, "FIND THE CELL AT C"),
      { kind: "escort", path: [{ node: "C" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OUT BEFORE THE BILL" },
    ],
    reward: { scrip: 490, xp: 980 },
    wasps: 2,
    mechs: 0,
  },
  {
    id: "g_walkway_post",
    kind: "gig",
    order: 0,
    title: "WALKWAY POST · LEASE ROW",
    level: "lease_row",
    fixer: "marrow",
    brief: "A LATTICE POST SITS PAST THE WALKWAY ON D, AND THE AUDIT CAN STILL SEE THE ROW THROUGH IT. CUT THAT POST, HOLD THE PLAZA, AND CLEAR THE WASPS THAT COME TO REPLACE IT.",
    objectives: [
      { kind: "destroy", spots: [{ past: "D", times: 2, dx: -1, dz: 1 }], label: "SENSOR POST", text: "CUT THE POST PAST THE WALKWAY" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 16, text: "HOLD THE PLAZA WHILE THE ROW GOES DARK", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT REPLACE THE POST" },
    ],
    reward: { scrip: 510, xp: 1020 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_crane_quiet",
    kind: "gig",
    order: 0,
    title: "CRANE QUIET · DOCKS",
    level: "deadletter_docks",
    fixer: "vessel",
    brief: "THREE TAPS STILL LISTEN ALONG THE CRANE LINE, AND THE ESTATE TRUSTS EVERY ONE OF THEM. CUT THE TAPS, HOLD THE PLAZA WHILE THE HARBOUR GOES QUIET, AND CLEAR THE REPAIR WASPS.",
    objectives: [
      { kind: "destroy", spots: [{ node: "B" }, { node: "D" }, { node: "E" }], label: "CABLE TAP", text: "CUT THE THREE CRANE TAPS" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE PLAZA WHILE THE HARBOUR GOES QUIET", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE REPAIR WASPS" },
    ],
    reward: { scrip: 530, xp: 1060 },
    wasps: 3,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_far_fence",
    kind: "gig",
    order: 0,
    title: "FAR FENCE · DEPOT",
    level: "repo_depot",
    fixer: "deacon",
    brief: "THE LOT ESCROW AT E IS RUNNING WHILE A MECH READS THE FAR FENCE. REACH THE TERMINAL, SURVIVE THE DUMP, AND DISABLE THE MECH BEFORE YOU LEAVE THROUGH THE PLAZA.",
    objectives: [
      reach({ node: "E" }, "REACH THE LOT ESCROW AT E"),
      { kind: "survive", seconds: 18, at: { node: "E" }, radius: 6, text: "HOLD THE DUMP", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH ON THE FAR FENCE" },
      reach({ node: "A" }, "OUT THROUGH THE PLAZA"),
    ],
    reward: { scrip: 550, xp: 1100 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_night_count",
    kind: "gig",
    order: 0,
    title: "NIGHT COUNT · NIGHT MARKET",
    level: "night_market",
    fixer: "marrow",
    brief: "THE NIGHT COUNT AT B IS A LEDGER THE STALLS KEEP OFF THE ESTATE FEED. HOLD THE RACK WHILE IT COPIES, THEN BREAK THE TWO POSTS THAT STILL WATCH THE AISLE.",
    objectives: [
      reach({ node: "B" }, "REACH THE NIGHT COUNT AT B"),
      { kind: "survive", seconds: 16, at: { node: "B" }, radius: 6, text: "HOLD THE RACK WHILE THE COUNT COPIES", waves: 1 },
      { kind: "destroy", spots: [{ node: "C" }, { node: "E" }], label: "SENSOR POST", text: "BREAK THE TWO POSTS ON THE AISLE" },
    ],
    reward: { scrip: 360, xp: 720 },
    wasps: 2,
    mechs: 0,
  },
  {
    id: "g_mast_walk",
    kind: "gig",
    order: 0,
    title: "MAST WALK · RELAY HEIGHTS",
    level: "relay_heights",
    fixer: "deacon",
    brief: "A CELL IS PINNED ON THE LOWER MAST AT D, AND THE NEXT UPLINK SWEEP WILL READ THEM. WALK THEM TO THE PLAZA AND CLEAR THE WASPS THAT RIDE THE TOWER.",
    objectives: [
      reach({ node: "D" }, "FIND THE CELL ON THE LOWER MAST AT D"),
      { kind: "escort", path: [{ node: "D" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM DOWN TO THE PLAZA" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS ON THE MAST" },
    ],
    reward: { scrip: 400, xp: 800 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_barge_shadow",
    kind: "gig",
    order: 0,
    title: "BARGE SHADOW · ASH CANAL",
    level: "ash_canal",
    fixer: "deacon",
    brief: "A WAKE CELL IS IN THE BARGE SHADOW AT E, AND THE SLUICE WILL NOT COVER THEM PAST THE NEXT PASS. WALK THEM TO THE PLAZA AND HOLD THE LOCK WHILE THE AUDIT DRONES READ THE WATER.",
    objectives: [
      reach({ node: "E" }, "FIND THE CELL IN THE BARGE SHADOW AT E"),
      { kind: "escort", path: [{ node: "E" }, { node: "A" }], speed: 2.1, leash: 8, who: "cell", text: "WALK THEM OFF THE WATER TO THE PLAZA" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE LOCK WHILE THE DRONES PASS", waves: 1 },
    ],
    reward: { scrip: 420, xp: 840 },
    wasps: 2,
    mechs: 0,
  },
  {
    id: "g_pane_skim",
    kind: "gig",
    order: 0,
    title: "PANE SKIM · GLASS MILE",
    level: "glass_mile",
    fixer: "marrow",
    brief: "THE SHOWROOM PANE AT C IS LEASING REFLECTIONS THE CLOCKEATERS NEVER SIGNED. CRACK IT, HOLD THE DUMP, AND DISABLE THE MECH THE GLASS CALLS BEFORE YOU LEAVE.",
    objectives: [
      reach({ node: "C" }, "CRACK THE SHOWROOM PANE AT C"),
      { kind: "survive", seconds: 18, at: { node: "C" }, radius: 6, text: "HOLD THE PANE WHILE THE REFLECTION DUMPS", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH THE GLASS CALLS" },
      reach({ node: "A" }, "OUT THROUGH THE PLAZA"),
    ],
    reward: { scrip: 440, xp: 880 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_floor_count",
    kind: "gig",
    order: 0,
    title: "FLOOR COUNT · BONE MARKET",
    level: "bone_market",
    fixer: "vessel",
    brief: "THREE LATTICE POSTS STILL PRICE THE CLINIC FLOOR, AND THE ESTATE AUDIT IS ALREADY INSIDE. BREAK THEM, HOLD THE PLAZA, AND CLEAR THE WASPS THAT COME TO RECOUNT THE BONES.",
    objectives: [
      { kind: "destroy", spots: [{ node: "B" }, { node: "D" }, { node: "E" }], label: "SENSOR POST", text: "BREAK THE THREE POSTS ON THE FLOOR" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE PLAZA WHILE THE COUNT DROPS", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT RECOUNT THE BONES" },
    ],
    reward: { scrip: 460, xp: 920 },
    wasps: 3,
    mechs: 0,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_cold_walk",
    kind: "gig",
    order: 0,
    title: "COLD WALK · COLD VAULT",
    level: "cold_vault",
    fixer: "deacon",
    brief: "A CELL IS LOCKED IN THE COLD AT D, AND THE BONDED DOOR WILL NOT STAY QUIET PAST THE NEXT SWEEP. WALK THEM TO THE PLAZA AND HOLD THE LOCK WHILE THE DOOR SEALS.",
    objectives: [
      reach({ node: "D" }, "FIND THE CELL IN THE COLD AT D"),
      { kind: "escort", path: [{ node: "D" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OUT OF THE COLD TO THE PLAZA" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 16, text: "HOLD THE LOCK WHILE THE DOOR SEALS", waves: 1 },
    ],
    reward: { scrip: 480, xp: 960 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_nave_skim",
    kind: "gig",
    order: 0,
    title: "NAVE SKIM · NEON CHAPEL",
    level: "neon_chapel",
    fixer: "marrow",
    brief: "THE OFFERING RACK AT B IS TAKING SLEEP CREDIT THE NAVE NEVER DECLARED. HOLD THE DUMP, CUT THE SENSOR ON THE FAR AISLE, AND GET OUT THROUGH THE PLAZA.",
    objectives: [
      reach({ node: "B" }, "REACH THE OFFERING RACK AT B"),
      { kind: "survive", seconds: 16, at: { node: "B" }, radius: 6, text: "HOLD THE RACK WHILE THE OFFERING DUMPS", waves: 1 },
      { kind: "destroy", spots: [{ node: "E" }], label: "SENSOR POST", text: "CUT THE SENSOR ON THE FAR AISLE" },
      reach({ node: "A" }, "OUT THROUGH THE PLAZA"),
    ],
    reward: { scrip: 500, xp: 1000 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_pour_posts",
    kind: "gig",
    order: 0,
    title: "POUR POSTS · SLAG PIT",
    level: "slag_pit",
    fixer: "vessel",
    brief: "THE SCALE AT THE PIT IS STILL WIRED TO THREE ESTATE POSTS, AND THE AUDIT READS EVERY POUR. BREAK THE POSTS, HOLD THE PLAZA WHILE THE SCALE GOES DARK, AND CLEAR THE WASPS THAT RELIGHT IT.",
    objectives: [
      { kind: "destroy", spots: [{ node: "B" }, { node: "C" }, { node: "D" }], label: "SENSOR POST", text: "BREAK THE THREE POSTS ON THE SCALE" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 20, text: "HOLD THE PLAZA WHILE THE SCALE GOES DARK", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT RELIGHT THE SCALE" },
    ],
    reward: { scrip: 520, xp: 1040 },
    wasps: 3,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_trellis_cell",
    kind: "gig",
    order: 0,
    title: "TRELLIS CELL · WIRE GARDEN",
    level: "wire_garden",
    fixer: "deacon",
    brief: "A CELL IS CAUGHT IN THE TRELLIS AT E, AND THE LIVE CABLE WILL TAG THEM ON THE NEXT PULSE. WALK THEM TO THE PLAZA AND CLEAR THE WASPS THAT FOLLOW THE SPARK.",
    objectives: [
      reach({ node: "E" }, "FIND THE CELL IN THE TRELLIS AT E"),
      { kind: "escort", path: [{ node: "E" }, { node: "A" }], speed: 2.3, leash: 8, who: "cell", text: "WALK THEM OUT OF THE TRELLIS TO THE PLAZA" },
      { kind: "kill", target: "wasp", count: 3, text: "CLEAR THE WASPS ON THE SPARK" },
    ],
    reward: { scrip: 540, xp: 1080 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_ping_dark",
    kind: "gig",
    order: 0,
    title: "PING DARK · BLACK RELAY",
    level: "black_relay",
    fixer: "vessel",
    brief: "TWO DISH POSTS STILL PING A RELAY THE ESTATE THINKS IS ALREADY DARK. BREAK THEM, HOLD THE PLAZA WHILE THE PING DIES, AND CLEAR THE WASPS THAT COME TO REWRITE IT.",
    objectives: [
      { kind: "destroy", spots: [{ node: "C" }, { node: "E" }], label: "SENSOR POST", text: "BREAK THE TWO DISH POSTS" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE PLAZA WHILE THE PING DIES", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT REWRITE THE PING" },
    ],
    reward: { scrip: 560, xp: 1120 },
    wasps: 3,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_rake_heat",
    kind: "gig",
    order: 0,
    title: "RAKE HEAT · RED KILN",
    level: "red_kiln",
    fixer: "marrow",
    brief: "THE RAKE AT B IS STILL READING THE KILN AFTER THE BISQUE SHIFT LEFT. HOLD THE DUMP, BREAK THE TWO POSTS ON THE FIRE LANE, AND CLEAR THE MECH THAT STAYS WITH THE HEAT.",
    objectives: [
      reach({ node: "B" }, "REACH THE RAKE AT B"),
      { kind: "survive", seconds: 16, at: { node: "B" }, radius: 6, text: "HOLD THE RAKE WHILE THE HEAT DUMPS", waves: 1 },
      { kind: "destroy", spots: [{ node: "D" }, { node: "E" }], label: "SENSOR POST", text: "BREAK THE TWO POSTS ON THE FIRE LANE" },
      { kind: "kill", target: "mech", count: 1, text: "CLEAR THE MECH ON THE HEAT" },
    ],
    reward: { scrip: 580, xp: 1160 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_wharf_wake",
    kind: "gig",
    order: 0,
    title: "WHARF WAKE · PAPER WHARF",
    level: "paper_wharf",
    fixer: "deacon",
    brief: "A CELL IS UNDER THE BALES AT D, AND THE NEXT CUSTOMS PASS WILL READ THEM. WALK THEM TO THE PLAZA AND HOLD THE WHARF WHILE THE MANIFEST DRONES GO BY.",
    objectives: [
      reach({ node: "D" }, "FIND THE CELL UNDER THE BALES AT D"),
      { kind: "escort", path: [{ node: "D" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OFF THE WHARF TO THE PLAZA" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE WHARF WHILE THE MANIFEST DRONES PASS", waves: 1 },
    ],
    reward: { scrip: 600, xp: 1200 },
    wasps: 2,
    mechs: 0,
  },
  {
    id: "g_court_skim",
    kind: "gig",
    order: 0,
    title: "COURT SKIM · VELVET COURT",
    level: "velvet_court",
    fixer: "marrow",
    brief: "THE PRIVATE RACK AT C IS LEASING NAMES THE COURT NEVER SIGNED. CRACK IT, HOLD THE DUMP, AND LEAVE THROUGH THE PLAZA BEFORE THE MEMBER DOOR REOPENS.",
    objectives: [
      reach({ node: "C" }, "CRACK THE PRIVATE RACK AT C"),
      { kind: "survive", seconds: 18, at: { node: "C" }, radius: 6, text: "HOLD THE RACK WHILE THE NAMES DUMP", waves: 1 },
      reach({ node: "A" }, "OUT THROUGH THE PLAZA"),
    ],
    reward: { scrip: 620, xp: 1240 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_crown_posts",
    kind: "gig",
    order: 0,
    title: "CROWN POSTS · RUST CROWN",
    level: "rust_crown",
    fixer: "vessel",
    brief: "THREE LATTICE POSTS STILL PRICE THE OXIDE ON THE CROWN, AND THE ESTATE AUDIT CAN SEE THE CLIMB. BREAK THEM, HOLD THE PLAZA WHILE THE FLAKE GOES DARK, AND CLEAR THE WASPS THAT RELIGHT IT.",
    objectives: [
      { kind: "destroy", spots: [{ node: "B" }, { node: "C" }, { node: "D" }], label: "SENSOR POST", text: "BREAK THE THREE POSTS ON THE CROWN" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE PLAZA WHILE THE FLAKE GOES DARK", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT RELIGHT THE CROWN" },
    ],
    reward: { scrip: 640, xp: 1280 },
    wasps: 3,
    mechs: 0,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_salt_cell",
    kind: "gig",
    order: 0,
    title: "SALT CELL · SALT STAIRS",
    level: "salt_stairs",
    fixer: "deacon",
    brief: "A CELL IS WHITED OUT ON THE LOWER STAIR AT E, AND THE NEXT LANDING SWEEP WILL READ THEM. WALK THEM TO THE PLAZA AND CLEAR THE WASPS THAT FOLLOW THE SALT.",
    objectives: [
      reach({ node: "E" }, "FIND THE CELL ON THE LOWER STAIR AT E"),
      { kind: "escort", path: [{ node: "E" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM UP TO THE PLAZA" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS ON THE SALT" },
    ],
    reward: { scrip: 660, xp: 1320 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_bazaar_rack",
    kind: "gig",
    order: 0,
    title: "BAZAAR RACK · LAMP BAZAAR",
    level: "lamp_bazaar",
    fixer: "marrow",
    brief: "THE SHADE RACK AT D IS SELLING WATTAGE THE BAZAAR NEVER DECLARED. HOLD THE DUMP, CUT THE SENSOR ON THE FAR STALL, AND GET OUT THROUGH THE PLAZA.",
    objectives: [
      reach({ node: "D" }, "REACH THE SHADE RACK AT D"),
      { kind: "survive", seconds: 16, at: { node: "D" }, radius: 6, text: "HOLD THE RACK WHILE THE WATTAGE DUMPS", waves: 1 },
      { kind: "destroy", spots: [{ node: "E" }], label: "SENSOR POST", text: "CUT THE SENSOR ON THE FAR STALL" },
      reach({ node: "A" }, "OUT THROUGH THE PLAZA"),
    ],
    reward: { scrip: 680, xp: 1360 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_bill_posts",
    kind: "gig",
    order: 0,
    title: "BILL POSTS · DEBT ORCHARD",
    level: "debt_orchard",
    fixer: "vessel",
    brief: "ROW FOUR STILL WIRES THE ORCHARD BILL TO TWO ESTATE POSTS. BREAK THEM, HOLD THE PLAZA WHILE THE BILL DROPS, AND CLEAR THE WASPS THAT COME TO REPRICE THE PICK.",
    objectives: [
      { kind: "destroy", spots: [{ node: "B" }, { node: "E" }], label: "SENSOR POST", text: "BREAK THE TWO POSTS ON ROW FOUR" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 20, text: "HOLD THE PLAZA WHILE THE BILL DROPS", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT REPRICE THE PICK" },
    ],
    reward: { scrip: 700, xp: 1400 },
    wasps: 3,
    mechs: 0,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_plaza_skim",
    kind: "gig",
    order: 0,
    title: "PLAZA SKIM · LEASE ROW",
    level: "lease_row",
    fixer: "deacon",
    brief: "THE SLEEP CREDIT AT C IS A SECOND LEDGER THE ROW KEEPS OFF THE WALKWAY. CRACK IT, HOLD THE DUMP, AND DISABLE THE MECH THE AUDIT SENDS BEFORE YOU LEAVE THROUGH B.",
    objectives: [
      reach({ node: "C" }, "CRACK THE LEDGER AT C"),
      { kind: "survive", seconds: 18, at: { node: "C" }, radius: 6, text: "HOLD THE DUMP", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH THE AUDIT SENDS" },
      reach({ node: "B" }, "OUT THROUGH B"),
    ],
    reward: { scrip: 720, xp: 1440 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_letter_hold",
    kind: "gig",
    order: 0,
    title: "LETTER HOLD · DOCKS",
    level: "deadletter_docks",
    fixer: "marrow",
    brief: "A CELL IS PINNED UNDER THE CRANE AT C, AND THE NEXT HARBOUR PASS WILL READ THEM. WALK THEM TO THE PLAZA AND HOLD THE LOCK WHILE THE LETTER DRONES GO BY.",
    objectives: [
      reach({ node: "C" }, "FIND THE CELL UNDER THE CRANE AT C"),
      { kind: "escort", path: [{ node: "C" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OFF THE CRANE TO THE PLAZA" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 16, text: "HOLD THE LOCK WHILE THE LETTER DRONES PASS", waves: 1 },
    ],
    reward: { scrip: 740, xp: 1480 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_lot_dark",
    kind: "gig",
    order: 0,
    title: "LOT DARK · DEPOT",
    level: "repo_depot",
    fixer: "vessel",
    brief: "TWO SENSOR POSTS STILL WATCH THE IMPOUND LOT FROM THE NEAR FENCE. BREAK THEM, HOLD THE PLAZA WHILE THE LOT GOES DARK, AND CLEAR THE WASPS THAT COME TO RELIGHT IT.",
    objectives: [
      { kind: "destroy", spots: [{ node: "B" }, { node: "D" }], label: "SENSOR POST", text: "BREAK THE TWO POSTS ON THE NEAR FENCE" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE PLAZA WHILE THE LOT GOES DARK", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT RELIGHT THE LOT" },
    ],
    reward: { scrip: 760, xp: 1520 },
    wasps: 3,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_lantern_quiet",
    kind: "gig",
    order: 0,
    title: "LANTERN QUIET · NIGHT MARKET",
    level: "night_market",
    fixer: "vessel",
    brief: "A LANTERN POST PAST THE STALLS AT D STILL READS THE MARKET AFTER THE NIGHT COUNT CLOSES. BREAK IT, HOLD THE PLAZA WHILE THE AISLE GOES QUIET, AND CLEAR THE MECH THAT COMES TO RELIGHT THE LANTERNS.",
    objectives: [
      { kind: "destroy", spots: [{ past: "D", times: 2, dx: -1, dz: 1 }], label: "LANTERN POST", text: "BREAK THE LANTERN POST PAST D" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE PLAZA WHILE THE AISLE GOES QUIET", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "CLEAR THE MECH THAT RELIGHTS THE LANTERNS" },
    ],
    reward: { scrip: 780, xp: 1560 },
    wasps: 2,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_crown_leash",
    kind: "gig",
    order: 0,
    title: "CROWN LEASH · RELAY HEIGHTS",
    level: "relay_heights",
    fixer: "marrow",
    brief: "THE CROWN LEASH AT C IS STILL RENTING THE MAST AFTER THE UPLINK SKIM. DOWN THE WASPS ON THE DISH, BREAK THE POST AT D, AND HOLD THE PLAZA WHILE THE LEASE DROPS.",
    objectives: [
      { kind: "kill", target: "wasp", count: 2, text: "DOWN THE WASPS ON THE CROWN DISH" },
      { kind: "destroy", spots: [{ node: "D" }], label: "SENSOR POST", text: "BREAK THE POST AT D" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 16, text: "HOLD THE PLAZA WHILE THE LEASE DROPS", waves: 1 },
    ],
    reward: { scrip: 800, xp: 1600 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_lock_ledger",
    kind: "gig",
    order: 0,
    title: "LOCK LEDGER · ASH CANAL",
    level: "ash_canal",
    fixer: "marrow",
    brief: "THE LOCK LEDGER AT D IS PRICING WATER THE CANAL NEVER DECLARED. CRACK IT, HOLD THE DUMP, AND DISABLE THE MECH THE SLUICE SENDS BEFORE YOU LEAVE THROUGH B.",
    objectives: [
      reach({ node: "D" }, "CRACK THE LOCK LEDGER AT D"),
      { kind: "survive", seconds: 18, at: { node: "D" }, radius: 6, text: "HOLD THE LEDGER WHILE THE WATER DUMPS", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH THE SLUICE SENDS" },
      reach({ node: "B" }, "OUT THROUGH B"),
    ],
    reward: { scrip: 820, xp: 1640 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_mile_dark",
    kind: "gig",
    order: 0,
    title: "MILE DARK · GLASS MILE",
    level: "glass_mile",
    fixer: "vessel",
    brief: "TWO LATTICE POSTS STILL PRICE THE MILE FROM THE SIDE STREETS AFTER THE PANE SKIM. BREAK THEM, HOLD THE PLAZA WHILE THE GLASS GOES DARK, AND CLEAR THE WASPS THAT COME TO REPOLISH IT.",
    objectives: [
      { kind: "destroy", spots: [{ node: "B" }, { node: "D" }], label: "SENSOR POST", text: "BREAK THE TWO POSTS ON THE SIDE STREETS" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE PLAZA WHILE THE GLASS GOES DARK", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT REPOLISH THE MILE" },
    ],
    reward: { scrip: 840, xp: 1680 },
    wasps: 3,
    mechs: 0,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_rib_walk",
    kind: "gig",
    order: 0,
    title: "RIB WALK · BONE MARKET",
    level: "bone_market",
    fixer: "deacon",
    brief: "A CELL IS UNDER THE RIB STALLS AT C, AND THE NEXT FLOOR COUNT WILL READ THEM. WALK THEM TO THE PLAZA AND DISABLE THE MECH THAT GUARDS THE AISLE.",
    objectives: [
      reach({ node: "C" }, "FIND THE CELL UNDER THE RIB STALLS AT C"),
      { kind: "escort", path: [{ node: "C" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OFF THE RIBS TO THE PLAZA" },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH ON THE AISLE" },
    ],
    reward: { scrip: 860, xp: 1720 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_frost_rack",
    kind: "gig",
    order: 0,
    title: "FROST RACK · COLD VAULT",
    level: "cold_vault",
    fixer: "marrow",
    brief: "THE FROST RACK AT E IS STILL BONDING CRATES THE VAULT NEVER SIGNED. HOLD THE DUMP, CUT THE POST AT C, AND GET OUT THROUGH THE PLAZA BEFORE THE DOOR SEALS.",
    objectives: [
      reach({ node: "E" }, "REACH THE FROST RACK AT E"),
      { kind: "survive", seconds: 16, at: { node: "E" }, radius: 6, text: "HOLD THE RACK WHILE THE CRATES DUMP", waves: 1 },
      { kind: "destroy", spots: [{ node: "C" }], label: "SENSOR POST", text: "CUT THE POST AT C" },
      reach({ node: "A" }, "OUT THROUGH THE PLAZA"),
    ],
    reward: { scrip: 880, xp: 1760 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_hymn_posts",
    kind: "gig",
    order: 0,
    title: "HYMN POSTS · NEON CHAPEL",
    level: "neon_chapel",
    fixer: "vessel",
    brief: "TWO POSTS IN THE SIDE AISLES STILL SING THE NAVE'S HYMN TO THE ESTATE. BREAK THEM, HOLD THE PLAZA WHILE THE HYMN DROPS, AND CLEAR THE WASPS THAT RESTART THE SERVICE.",
    objectives: [
      { kind: "destroy", spots: [{ node: "C" }, { node: "D" }], label: "SENSOR POST", text: "BREAK THE TWO POSTS IN THE SIDE AISLES" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 20, text: "HOLD THE PLAZA WHILE THE HYMN DROPS", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT RESTART THE HYMN" },
    ],
    reward: { scrip: 900, xp: 1800 },
    wasps: 3,
    mechs: 0,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_grate_cell",
    kind: "gig",
    order: 0,
    title: "GRATE CELL · SLAG PIT",
    level: "slag_pit",
    fixer: "deacon",
    brief: "A CELL IS UNDER THE GRATE AT C, AND THE NEXT POUR WILL READ THEIR HEAT. WALK THEM TO THE PLAZA AND HOLD THE PIT WHILE THE SCALE DRONES PASS.",
    objectives: [
      reach({ node: "C" }, "FIND THE CELL UNDER THE GRATE AT C"),
      { kind: "escort", path: [{ node: "C" }, { node: "A" }], speed: 2.1, leash: 8, who: "cell", text: "WALK THEM OFF THE GRATE TO THE PLAZA" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE PIT WHILE THE SCALE DRONES PASS", waves: 1 },
    ],
    reward: { scrip: 920, xp: 1840 },
    wasps: 2,
    mechs: 0,
  },
  {
    id: "g_root_skim",
    kind: "gig",
    order: 0,
    title: "ROOT SKIM · WIRE GARDEN",
    level: "wire_garden",
    fixer: "marrow",
    brief: "THE ROOT RACK AT D IS SKIMMING SPARK THE GARDEN NEVER DECLARED. CRACK IT, HOLD THE DUMP, AND DISABLE THE MECH THE TRELLIS CALLS BEFORE THE PULSE RETURNS.",
    objectives: [
      reach({ node: "D" }, "CRACK THE ROOT RACK AT D"),
      { kind: "survive", seconds: 16, at: { node: "D" }, radius: 6, text: "HOLD THE RACK WHILE THE SPARK DUMPS", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH THE TRELLIS CALLS" },
    ],
    reward: { scrip: 940, xp: 1880 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_blank_dish",
    kind: "gig",
    order: 0,
    title: "BLANK DISH · BLACK RELAY",
    level: "black_relay",
    fixer: "deacon",
    brief: "A CELL IS PINNED UNDER THE BLANK DISH AT B, AND THE NEXT PING WILL NAME THEM. WALK THEM TO THE PLAZA AND HOLD THE RELAY WHILE THE SILENCE DRONES PASS.",
    objectives: [
      reach({ node: "B" }, "FIND THE CELL UNDER THE BLANK DISH AT B"),
      { kind: "escort", path: [{ node: "B" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OFF THE DISH TO THE PLAZA" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 16, text: "HOLD THE RELAY WHILE THE SILENCE DRONES PASS", waves: 1 },
    ],
    reward: { scrip: 960, xp: 1920 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_glaze_walk",
    kind: "gig",
    order: 0,
    title: "GLAZE WALK · RED KILN",
    level: "red_kiln",
    fixer: "vessel",
    brief: "THE GLAZE LINE AT E IS STILL PRICING HEAT AFTER THE RAKE WENT QUIET. HOLD THE MOUTH WHILE THE LINE DUMPS, BREAK THE POST AT C, AND CLEAR THE MECH THAT STAYS ON THE GLAZE.",
    objectives: [
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE MOUTH WHILE THE GLAZE SETS", waves: 1 },
      reach({ node: "E" }, "REACH THE GLAZE LINE AT E"),
      { kind: "destroy", spots: [{ node: "C" }], label: "GLAZE POST", text: "BREAK THE POST THAT READS THE LINE" },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH ON THE GLAZE" },
    ],
    reward: { scrip: 980, xp: 1960 },
    wasps: 2,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_shear_quiet",
    kind: "gig",
    order: 0,
    title: "SHEAR QUIET · PAPER WHARF",
    level: "paper_wharf",
    fixer: "vessel",
    brief: "THE SHEAR AT C STILL CUTS PAPER THE WHARF NEVER DECLARED. BREAK THE TWO POSTS ON THE BLADES, HOLD THE PLAZA WHILE THEY STOP, AND CLEAR THE WASPS THAT RESTART THE SHEAR.",
    objectives: [
      { kind: "destroy", spots: [{ node: "C" }, { node: "E" }], label: "SHEAR POST", text: "BREAK THE TWO POSTS ON THE BLADES" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE PLAZA WHILE THE SHEAR STOPS", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT RESTART THE SHEAR" },
    ],
    reward: { scrip: 1000, xp: 2000 },
    wasps: 3,
    mechs: 0,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_cover_cell",
    kind: "gig",
    order: 0,
    title: "COVER CELL · VELVET COURT",
    level: "velvet_court",
    fixer: "deacon",
    brief: "A CELL IS UNDER THE COVER AT E, AND THE NEXT MEMBER PASS WILL READ THEM. WALK THEM TO THE PLAZA AND DISABLE THE MECH ON THE DOOR BEFORE THE COVER REOPENS.",
    objectives: [
      reach({ node: "E" }, "FIND THE CELL UNDER THE COVER AT E"),
      { kind: "escort", path: [{ node: "E" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OFF THE COVER TO THE PLAZA" },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH ON THE MEMBER DOOR" },
    ],
    reward: { scrip: 1020, xp: 2040 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_oxide_rack",
    kind: "gig",
    order: 0,
    title: "OXIDE RACK · RUST CROWN",
    level: "rust_crown",
    fixer: "marrow",
    brief: "THE OXIDE RACK AT B IS LEASING FLAKE THE CROWN NEVER SIGNED. HOLD THE DUMP, CUT THE POST AT D, AND GET OUT THROUGH THE PLAZA BEFORE THE CLIMB REOPENS.",
    objectives: [
      reach({ node: "B" }, "REACH THE OXIDE RACK AT B"),
      { kind: "survive", seconds: 16, at: { node: "B" }, radius: 6, text: "HOLD THE RACK WHILE THE FLAKE DUMPS", waves: 1 },
      { kind: "destroy", spots: [{ node: "D" }], label: "SENSOR POST", text: "CUT THE POST AT D" },
      reach({ node: "A" }, "OUT THROUGH THE PLAZA"),
    ],
    reward: { scrip: 1040, xp: 2080 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_landing_dark",
    kind: "gig",
    order: 0,
    title: "LANDING DARK · SALT STAIRS",
    level: "salt_stairs",
    fixer: "vessel",
    brief: "TWO POSTS ON THE UPPER LANDING STILL PRICE THE WHITE OUT AFTER THE STAIRS CLEAR. BREAK THEM, HOLD THE PLAZA WHILE THE LANDING GOES DARK, AND CLEAR THE MECH THAT RELIGHTS THE SALT.",
    objectives: [
      { kind: "destroy", spots: [{ node: "B" }, { node: "C" }], label: "SENSOR POST", text: "BREAK THE TWO POSTS ON THE UPPER LANDING" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 20, text: "HOLD THE PLAZA WHILE THE LANDING GOES DARK", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "CLEAR THE MECH THAT RELIGHTS THE SALT" },
    ],
    reward: { scrip: 1060, xp: 2120 },
    wasps: 2,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_filament_cell",
    kind: "gig",
    order: 0,
    title: "FILAMENT CELL · LAMP BAZAAR",
    level: "lamp_bazaar",
    fixer: "deacon",
    brief: "A CELL IS CAUGHT IN THE FILAMENT AT E, AND THE NEXT SHADE SWEEP WILL READ THEM. WALK THEM TO THE PLAZA AND HOLD THE BAZAAR WHILE THE BULB DRONES PASS.",
    objectives: [
      reach({ node: "E" }, "FIND THE CELL IN THE FILAMENT AT E"),
      { kind: "escort", path: [{ node: "E" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OUT OF THE FILAMENT TO THE PLAZA" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE BAZAAR WHILE THE BULB DRONES PASS", waves: 1 },
    ],
    reward: { scrip: 1080, xp: 2160 },
    wasps: 2,
    mechs: 0,
  },
  {
    id: "g_stem_ledger",
    kind: "gig",
    order: 0,
    title: "STEM LEDGER · DEBT ORCHARD",
    level: "debt_orchard",
    fixer: "marrow",
    brief: "THE STEM LEDGER AT D IS PRICING FRUIT THE ORCHARD NEVER WEIGHED. CRACK IT, HOLD THE DUMP, AND DISABLE THE MECH THAT GUARDS THE SCALE.",
    objectives: [
      reach({ node: "D" }, "CRACK THE STEM LEDGER AT D"),
      { kind: "survive", seconds: 16, at: { node: "D" }, radius: 6, text: "HOLD THE LEDGER WHILE THE FRUIT DUMPS", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH ON THE SCALE" },
    ],
    reward: { scrip: 1100, xp: 2200 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_cage_file",
    kind: "gig",
    order: 0,
    title: "CAGE FILE · LEASE ROW",
    level: "lease_row",
    fixer: "vessel",
    brief: "A FILING CAGE AT E STILL HOLDS A CELL THE ROW THINKS IS CLOSED. BREAK THE CAGE POST, WALK THEM TO THE PLAZA, AND HOLD THE ROW WHILE THE FILE DROPS.",
    objectives: [
      { kind: "destroy", spots: [{ node: "B" }], label: "CAGE POST", text: "BREAK THE CAGE POST AT B" },
      reach({ node: "E" }, "FIND THE CELL IN THE FILING CAGE AT E"),
      { kind: "escort", path: [{ node: "E" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OFF THE CAGE TO THE PLAZA" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 16, text: "HOLD THE PLAZA WHILE THE FILE DROPS", waves: 1 },
    ],
    reward: { scrip: 1120, xp: 2240 },
    wasps: 2,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_tide_rack",
    kind: "gig",
    order: 0,
    title: "TIDE RACK · DOCKS",
    level: "deadletter_docks",
    fixer: "deacon",
    brief: "THE TIDE RACK AT B IS STILL NAMING LETTERS THE HARBOUR NEVER FILED. HOLD THE DUMP, BREAK THE POST AT E, AND CLEAR THE MECH THE CRANE SENDS.",
    objectives: [
      reach({ node: "B" }, "REACH THE TIDE RACK AT B"),
      { kind: "survive", seconds: 18, at: { node: "B" }, radius: 6, text: "HOLD THE RACK WHILE THE TIDE DUMPS", waves: 1 },
      { kind: "destroy", spots: [{ node: "E" }], label: "SENSOR POST", text: "BREAK THE POST AT E" },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH THE CRANE SENDS" },
    ],
    reward: { scrip: 1140, xp: 2280 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_impound_walk",
    kind: "gig",
    order: 0,
    title: "IMPOUND WALK · DEPOT",
    level: "repo_depot",
    fixer: "marrow",
    brief: "A CELL IS IN THE CAGE ROW AT D, AND THE NEXT LOT COUNT WILL READ THEM. WALK THEM TO THE PLAZA, HOLD THE LOT, AND CLEAR THE WASPS ON THE FENCE.",
    objectives: [
      reach({ node: "D" }, "FIND THE CELL IN THE CAGE ROW AT D"),
      { kind: "escort", path: [{ node: "D" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OUT OF THE CAGE ROW TO THE PLAZA" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE LOT WHILE THE COUNT PASSES", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS ON THE FENCE" },
    ],
    reward: { scrip: 1160, xp: 2320 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_spice_crate",
    kind: "gig",
    order: 0,
    title: "SPICE CRATE · NIGHT MARKET",
    level: "night_market",
    fixer: "deacon",
    brief: "THE SPICE CRATE AT E IS A CELL THE NIGHT COUNT MISSED AFTER THE LANTERNS WENT QUIET. REACH THE CRATE, HOLD THE AISLE WHILE IT OPENS, AND WALK THEM OUT THROUGH B BEFORE THE STALLS REOPEN.",
    objectives: [
      reach({ node: "E" }, "FIND THE CELL IN THE SPICE CRATE AT E"),
      { kind: "hold", at: { node: "E" }, radius: 6, seconds: 14, text: "HOLD THE AISLE WHILE THE CRATE OPENS", waves: 1 },
      { kind: "escort", path: [{ node: "E" }, { node: "B" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OUT THROUGH B" },
    ],
    reward: { scrip: 1180, xp: 2360 },
    wasps: 2,
    mechs: 0,
  },
  {
    id: "g_guy_wire",
    kind: "gig",
    order: 0,
    title: "GUY WIRE · RELAY HEIGHTS",
    level: "relay_heights",
    fixer: "vessel",
    brief: "TWO GUY WIRES ON THE LOWER MAST STILL HOLD A DISH THE HEIGHTS THOUGHT WAS CUT. BREAK THEM, SURVIVE THE SWAY AT B, AND CLEAR THE MECH THAT CLIMBS TO RETENSION THE CROWN.",
    objectives: [
      { kind: "destroy", spots: [{ node: "C" }, { node: "E" }], label: "GUY WIRE", text: "BREAK THE TWO GUY WIRES" },
      { kind: "survive", seconds: 16, at: { node: "B" }, radius: 6, text: "SURVIVE THE SWAY AT B", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "CLEAR THE MECH ON THE CROWN" },
    ],
    reward: { scrip: 1200, xp: 2400 },
    wasps: 2,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_ash_weir",
    kind: "gig",
    order: 0,
    title: "ASH WEIR · ASH CANAL",
    level: "ash_canal",
    fixer: "vessel",
    brief: "THE WEIR AT C IS STILL METERING ASH THE CANAL NEVER FILED. HOLD THE WEIR WHILE THE METER DUMPS, BREAK THE POST AT B, AND CLEAR THE WASPS THAT COME UP THE SLUICE.",
    objectives: [
      { kind: "hold", at: { node: "C" }, radius: 7, seconds: 16, text: "HOLD THE WEIR WHILE THE METER DUMPS", waves: 1 },
      { kind: "destroy", spots: [{ node: "B" }], label: "WEIR POST", text: "BREAK THE POST AT B" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS ON THE SLUICE" },
    ],
    reward: { scrip: 1220, xp: 2440 },
    wasps: 3,
    mechs: 0,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_lintel_file",
    kind: "gig",
    order: 0,
    title: "LINTEL FILE · GLASS MILE",
    level: "glass_mile",
    fixer: "marrow",
    brief: "A FILING LINTEL AT B STILL HOLDS A CELL THE MILE THINKS IS WINDOW DRESSING. BREAK THE LINTEL POST AT D, WALK THEM FROM B TO THE PLAZA, AND HOLD THE GLASS WHILE THE FILE DROPS.",
    objectives: [
      { kind: "destroy", spots: [{ node: "D" }], label: "LINTEL POST", text: "BREAK THE LINTEL POST AT D" },
      reach({ node: "B" }, "FIND THE CELL AT THE LINTEL AT B"),
      { kind: "escort", path: [{ node: "B" }, { node: "A" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM FROM THE LINTEL TO THE PLAZA" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 16, text: "HOLD THE GLASS WHILE THE FILE DROPS", waves: 1 },
    ],
    reward: { scrip: 1240, xp: 2480 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_marrow_bin",
    kind: "gig",
    order: 0,
    title: "MARROW BIN · BONE MARKET",
    level: "bone_market",
    fixer: "deacon",
    brief: "THE MARROW BIN PAST C IS WEIGHING BONES THE CLINIC NEVER SIGNED. REACH IT, SURVIVE THE WEIGH, AND DISABLE THE MECH THAT GUARDS THE BIN BEFORE YOU LEAVE THROUGH E.",
    objectives: [
      reach({ past: "C", times: 2, dx: 1, dz: -1 }, "REACH THE MARROW BIN PAST C"),
      { kind: "survive", seconds: 16, at: { past: "C", times: 2, dx: 1, dz: -1 }, radius: 6, text: "HOLD THE BIN WHILE THE BONES WEIGH", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH ON THE BIN" },
      reach({ node: "E" }, "OUT THROUGH E"),
    ],
    reward: { scrip: 1260, xp: 2520 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_seal_break",
    kind: "gig",
    order: 0,
    title: "SEAL BREAK · COLD VAULT",
    level: "cold_vault",
    fixer: "vessel",
    brief: "THE INNER SEAL AT B IS STILL CALLING A CONVOY THE VAULT ALREADY CLOSED. BREAK THE SEAL POST, HOLD THE PLAZA WHILE THE CALL DIES, AND CLEAR THE WASPS AND THE MECH THAT ANSWER IT.",
    objectives: [
      { kind: "destroy", spots: [{ node: "B" }], label: "SEAL POST", text: "BREAK THE SEAL POST AT B" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE PLAZA WHILE THE CALL DIES", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT ANSWER THE SEAL" },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH THAT ANSWERS THE SEAL" },
    ],
    reward: { scrip: 1280, xp: 2560 },
    wasps: 3,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_choir_wire",
    kind: "gig",
    order: 0,
    title: "CHOIR WIRE · NEON CHAPEL",
    level: "neon_chapel",
    fixer: "marrow",
    brief: "A CHOIR WIRE PAST THE NAVE AT E IS STILL SINGING NAMES THE CHAPEL NEVER BURIED. CRACK IT, HOLD THE WIRE WHILE THE NAMES DUMP, AND WALK THE CELL UNDER IT OUT THROUGH C.",
    objectives: [
      reach({ past: "E", times: 2, dx: -1, dz: 1 }, "CRACK THE CHOIR WIRE PAST E"),
      { kind: "survive", seconds: 16, at: { past: "E", times: 2, dx: -1, dz: 1 }, radius: 6, text: "HOLD THE WIRE WHILE THE NAMES DUMP", waves: 1 },
      { kind: "escort", path: [{ past: "E", times: 2, dx: -1, dz: 1 }, { node: "C" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THE CELL OUT THROUGH C" },
    ],
    reward: { scrip: 1300, xp: 2600 },
    wasps: 2,
    mechs: 0,
  },
  {
    id: "g_skim_ladle",
    kind: "gig",
    order: 0,
    title: "SKIM LADLE · SLAG PIT",
    level: "slag_pit",
    fixer: "marrow",
    brief: "THE LADLE AT B IS SKIMMING SLAG THE PIT NEVER WEIGHED. REACH THE LADLE, BREAK THE TWO POSTS THAT PRICE THE SKIM, AND HOLD THE PLAZA WHILE THE LADLE COOLS.",
    objectives: [
      reach({ node: "B" }, "REACH THE LADLE AT B"),
      { kind: "destroy", spots: [{ node: "C" }, { node: "D" }], label: "SKIM POST", text: "BREAK THE TWO POSTS THAT PRICE THE SKIM" },
      { kind: "hold", at: { node: "A" }, radius: 8, seconds: 18, text: "HOLD THE PLAZA WHILE THE LADLE COOLS", waves: 1 },
    ],
    reward: { scrip: 1320, xp: 2640 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_coil_quiet",
    kind: "gig",
    order: 0,
    title: "COIL QUIET · WIRE GARDEN",
    level: "wire_garden",
    fixer: "vessel",
    brief: "A COIL AT C IS STILL LIVE AFTER THE ROOT SKIM. HOLD THE COIL WHILE IT GROUNDS, CLEAR THE MECH ON THE SPARK, AND WALK OUT THROUGH E BEFORE THE PULSE RETURNS.",
    objectives: [
      { kind: "hold", at: { node: "C" }, radius: 6, seconds: 16, text: "HOLD THE COIL WHILE IT GROUNDS", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "CLEAR THE MECH ON THE SPARK" },
      reach({ node: "E" }, "OUT THROUGH E"),
    ],
    reward: { scrip: 1340, xp: 2680 },
    wasps: 2,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_null_tape",
    kind: "gig",
    order: 0,
    title: "NULL TAPE · BLACK RELAY",
    level: "black_relay",
    fixer: "deacon",
    brief: "THE NULL TAPE AT D IS A LEASE THE RELAY NEVER ERASED. CRACK IT, SURVIVE THE SILENCE, BREAK THE DISH POST AT C, AND CLEAR THE WASPS THAT TRY TO REWRITE THE TAPE.",
    objectives: [
      reach({ node: "D" }, "CRACK THE NULL TAPE AT D"),
      { kind: "survive", seconds: 18, at: { node: "D" }, radius: 6, text: "HOLD THE TAPE WHILE THE SILENCE WRITES", waves: 1 },
      { kind: "destroy", spots: [{ node: "C" }], label: "DISH POST", text: "BREAK THE DISH POST AT C" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT REWRITE THE TAPE" },
    ],
    reward: { scrip: 1360, xp: 2720 },
    wasps: 3,
    mechs: 1,
  },
  {
    id: "g_vent_sag",
    kind: "gig",
    order: 0,
    title: "VENT SAG · RED KILN",
    level: "red_kiln",
    fixer: "deacon",
    brief: "THE COOLING VENT AT D IS STILL FEEDING THE KILN A DRAFT THE BISQUE SHIFT NEVER SIGNED. WALK THE CELL OFF THE VENT TO B, HOLD THE LANDING WHILE THE DRAFT DIES, AND CLEAR THE WASPS THAT RELIGHT THE FIRE.",
    objectives: [
      reach({ node: "D" }, "FIND THE CELL ON THE COOLING VENT AT D"),
      { kind: "escort", path: [{ node: "D" }, { node: "B" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OFF THE VENT TO B" },
      { kind: "hold", at: { node: "B" }, radius: 7, seconds: 16, text: "HOLD THE LANDING WHILE THE DRAFT DIES", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT RELIGHT THE FIRE" },
    ],
    reward: { scrip: 1380, xp: 2760 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_ink_barge",
    kind: "gig",
    order: 0,
    title: "INK BARGE · PAPER WHARF",
    level: "paper_wharf",
    fixer: "marrow",
    brief: "THE INK BARGE AT D IS PRINTING A MANIFEST THE WHARF ALREADY REFUSED. HOLD THE BARGE WHILE THE TYPE COOLS, BREAK THE POST AT B, AND CLEAR THE WASPS THAT RESTART THE PRESS.",
    objectives: [
      { kind: "hold", at: { node: "D" }, radius: 6, seconds: 16, text: "HOLD THE BARGE WHILE THE TYPE COOLS", waves: 1 },
      { kind: "destroy", spots: [{ node: "B" }], label: "INK POST", text: "BREAK THE POST AT B" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT RESTART THE PRESS" },
    ],
    reward: { scrip: 1400, xp: 2800 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_guest_book",
    kind: "gig",
    order: 0,
    title: "GUEST BOOK · VELVET COURT",
    level: "velvet_court",
    fixer: "vessel",
    brief: "A PRIVATE MECH IS STILL READING THE GUEST BOOK AT E AFTER THE COVER CLOSED. DISABLE IT, BREAK THE BOOK POST AT C, AND HOLD THE COURT AT B WHILE THE NAMES GO DARK.",
    objectives: [
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH ON THE GUEST BOOK" },
      { kind: "destroy", spots: [{ node: "C" }], label: "BOOK POST", text: "BREAK THE BOOK POST AT C" },
      { kind: "hold", at: { node: "B" }, radius: 8, seconds: 18, text: "HOLD THE COURT WHILE THE NAMES GO DARK", waves: 1 },
    ],
    reward: { scrip: 1420, xp: 2840 },
    wasps: 2,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_climb_cell",
    kind: "gig",
    order: 0,
    title: "CLIMB CELL · RUST CROWN",
    level: "rust_crown",
    fixer: "deacon",
    brief: "A CELL IS HALFWAY UP THE SERVICE CLIMB AT C, AND THE FLAKE WILL NOT HIDE THEM PAST THE NEXT OXIDE PASS. WALK THEM DOWN TO B AND HOLD THE LANDING WHILE THE MECH THAT FOLLOWS IS DISABLED.",
    objectives: [
      reach({ node: "C" }, "FIND THE CELL ON THE SERVICE CLIMB AT C"),
      { kind: "escort", path: [{ node: "C" }, { node: "B" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM DOWN THE CLIMB TO B" },
      { kind: "hold", at: { node: "B" }, radius: 7, seconds: 16, text: "HOLD THE LANDING WHILE THE CLIMB CLEARS", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH THAT FOLLOWS THE CLIMB" },
    ],
    reward: { scrip: 1440, xp: 2880 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_tread_count",
    kind: "gig",
    order: 0,
    title: "TREAD COUNT · SALT STAIRS",
    level: "salt_stairs",
    fixer: "marrow",
    brief: "THE TREAD LEDGER AT C IS PRICING STEPS THE STAIRS NEVER COUNTED. CRACK IT, SURVIVE THE COUNT, BREAK THE POST AT E, AND CLEAR THE WASPS THAT RECOUNT THE SALT.",
    objectives: [
      reach({ node: "C" }, "CRACK THE TREAD LEDGER AT C"),
      { kind: "survive", seconds: 16, at: { node: "C" }, radius: 6, text: "HOLD THE COUNT WHILE THE STEPS DUMP", waves: 1 },
      { kind: "destroy", spots: [{ node: "E" }], label: "TREAD POST", text: "BREAK THE POST AT E" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT RECOUNT THE SALT" },
    ],
    reward: { scrip: 1460, xp: 2920 },
    wasps: 3,
    mechs: 1,
  },
  {
    id: "g_watt_shade",
    kind: "gig",
    order: 0,
    title: "WATT SHADE · LAMP BAZAAR",
    level: "lamp_bazaar",
    fixer: "vessel",
    brief: "THE WATT SHADE AT B IS STILL LEASING BULBS THE BAZAAR CLOSED AFTER THE FILAMENT WALK. HOLD THE SHADE WHILE THE WATTAGE DROPS, BREAK THE POST AT D, AND CLEAR THE MECH ON THE BULB.",
    objectives: [
      { kind: "hold", at: { node: "B" }, radius: 6, seconds: 16, text: "HOLD THE SHADE WHILE THE WATTAGE DROPS", waves: 1 },
      { kind: "destroy", spots: [{ node: "D" }], label: "SHADE POST", text: "BREAK THE POST AT D" },
      { kind: "kill", target: "mech", count: 1, text: "CLEAR THE MECH ON THE BULB" },
    ],
    reward: { scrip: 1480, xp: 2960 },
    wasps: 2,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_graft_walk",
    kind: "gig",
    order: 0,
    title: "GRAFT WALK · DEBT ORCHARD",
    level: "debt_orchard",
    fixer: "deacon",
    brief: "A CELL IS GRAFTED INTO ROW TWO AT B, AND THE NEXT BILL WILL READ THE FRUIT AS THEIRS. WALK THEM TO E, HOLD THE ROW WHILE THE GRAFT DROPS, AND CLEAR THE WASPS IN THE TREES.",
    objectives: [
      reach({ node: "B" }, "FIND THE CELL GRAFTED INTO ROW TWO AT B"),
      { kind: "escort", path: [{ node: "B" }, { node: "E" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM FROM ROW TWO TO E" },
      { kind: "hold", at: { node: "E" }, radius: 7, seconds: 16, text: "HOLD THE ROW WHILE THE GRAFT DROPS", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS IN THE TREES" },
    ],
    reward: { scrip: 1500, xp: 3000 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_sleep_stair",
    kind: "gig",
    order: 0,
    title: "SLEEP STAIR · LEASE ROW",
    level: "lease_row",
    fixer: "marrow",
    brief: "THE SLEEP STAIR AT E IS A THIRD LEDGER THE ROW KEEPS OFF BOTH THE WALKWAY AND THE PLAZA. CRACK IT, HOLD THE STAIR WHILE IT DUMPS, BREAK THE POST AT C, AND LEAVE THROUGH D.",
    objectives: [
      reach({ node: "E" }, "CRACK THE SLEEP STAIR AT E"),
      { kind: "hold", at: { node: "E" }, radius: 6, seconds: 16, text: "HOLD THE STAIR WHILE THE LEDGER DUMPS", waves: 1 },
      { kind: "destroy", spots: [{ node: "C" }], label: "STAIR POST", text: "BREAK THE POST AT C" },
      reach({ node: "D" }, "OUT THROUGH D"),
    ],
    reward: { scrip: 1520, xp: 3040 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_pier_witness",
    kind: "gig",
    order: 0,
    title: "PIER WITNESS · DOCKS",
    level: "deadletter_docks",
    fixer: "vessel",
    brief: "A WITNESS CELL IS ON THE OUTER PIER AT D, AND THE NEXT LETTER PASS WILL FILE THEM AS CARGO. WALK THEM TO B, HOLD THE PIER WHILE THE CRANE TURNS, AND CLEAR THE WASPS THAT READ THE WAKE.",
    objectives: [
      reach({ node: "D" }, "FIND THE WITNESS CELL ON THE OUTER PIER AT D"),
      { kind: "escort", path: [{ node: "D" }, { node: "B" }], speed: 2.2, leash: 8, who: "cell", text: "WALK THEM OFF THE PIER TO B" },
      { kind: "hold", at: { node: "B" }, radius: 7, seconds: 16, text: "HOLD THE PIER WHILE THE CRANE TURNS", waves: 1 },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT READ THE WAKE" },
    ],
    reward: { scrip: 1540, xp: 3080 },
    wasps: 3,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_clamp_quiet",
    kind: "gig",
    order: 0,
    title: "CLAMP QUIET · DEPOT",
    level: "repo_depot",
    fixer: "deacon",
    brief: "THE WHEEL CLAMP AT C IS STILL PRICING CARS THE LOT ALREADY RELEASED. BREAK THE CLAMP POST, HOLD THE STALL WHILE THE PRICE DROPS, DISABLE THE MECH THAT RELOCKS THE ROW, AND GET OUT THROUGH THE PLAZA.",
    objectives: [
      { kind: "destroy", spots: [{ node: "C" }], label: "CLAMP POST", text: "BREAK THE CLAMP POST AT C" },
      { kind: "hold", at: { node: "C" }, radius: 6, seconds: 16, text: "HOLD THE STALL WHILE THE PRICE DROPS", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH THAT RELOCKS THE ROW" },
      reach({ node: "A" }, "OUT THROUGH THE PLAZA"),
    ],
    reward: { scrip: 1560, xp: 3120 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_close_bell",
    kind: "gig",
    order: 0,
    title: "CLOSE BELL · NIGHT MARKET",
    level: "night_market",
    fixer: "marrow",
    brief: "THE CLOSE BELL AT D IS STILL RINGING A LEASE THE STALLS ALREADY PAID. HOLD THE BELL WHILE THE CHIME DIES, BREAK THE POST AT B, AND CLEAR THE WASPS THAT RESTART IT.",
    objectives: [
      { kind: "hold", at: { node: "D" }, radius: 6, seconds: 16, text: "HOLD THE BELL WHILE THE CHIME DIES", waves: 1 },
      { kind: "destroy", spots: [{ node: "B" }], label: "BELL POST", text: "BREAK THE POST AT B" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT RESTART THE CHIME" },
    ],
    reward: { scrip: 1580, xp: 3160 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_static_well",
    kind: "gig",
    order: 0,
    title: "STATIC WELL · RELAY HEIGHTS",
    level: "relay_heights",
    fixer: "deacon",
    brief: "THE STATIC WELL AT E IS STILL DRAWING A BEACON THE HEIGHTS SHUT AFTER THE GUY WIRES CAME DOWN. REACH THE WELL, HOLD IT WHILE THE DRAW DIES, BREAK THE POST AT B, AND CLEAR THE WASPS THAT REFEED THE DISH.",
    objectives: [
      reach({ node: "E" }, "REACH THE STATIC WELL AT E"),
      { kind: "hold", at: { node: "E" }, radius: 6, seconds: 16, text: "HOLD THE WELL WHILE THE DRAW DIES", waves: 1 },
      { kind: "destroy", spots: [{ node: "B" }], label: "DISH POST", text: "BREAK THE POST AT B" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT REFEED THE DISH" },
    ],
    reward: { scrip: 1600, xp: 3200 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_draft_mark",
    kind: "gig",
    order: 0,
    title: "DRAFT MARK · ASH CANAL",
    level: "ash_canal",
    fixer: "marrow",
    brief: "THE DRAFT MARK AT B IS PRICING A HULL THE CANAL ALREADY SANK. CRACK THE MARK, SURVIVE THE READING, BREAK THE POST AT E, AND CLEAR THE WASPS THAT REWRITE THE DRAFT.",
    objectives: [
      reach({ node: "B" }, "CRACK THE DRAFT MARK AT B"),
      { kind: "survive", seconds: 16, at: { node: "B" }, radius: 6, text: "HOLD THE MARK WHILE THE DRAFT READS", waves: 1 },
      { kind: "destroy", spots: [{ node: "E" }], label: "DRAFT POST", text: "BREAK THE POST AT E" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT REWRITE THE DRAFT" },
    ],
    reward: { scrip: 1620, xp: 3240 },
    wasps: 3,
    mechs: 0,
  },
  {
    id: "g_curtain_wall",
    kind: "gig",
    order: 0,
    title: "CURTAIN WALL · GLASS MILE",
    level: "glass_mile",
    fixer: "vessel",
    brief: "THE CURTAIN WALL AT C IS STILL REFLECTING A LEASE THE MILE TOOK DOWN WITH THE PANES. HOLD THE WALL WHILE THE REFLECTION DIES, BREAK THE POST AT E, AND CLEAR THE MECH THAT REPOLISHES THE GLASS.",
    objectives: [
      { kind: "hold", at: { node: "C" }, radius: 7, seconds: 16, text: "HOLD THE WALL WHILE THE REFLECTION DIES", waves: 1 },
      { kind: "destroy", spots: [{ node: "E" }], label: "WALL POST", text: "BREAK THE POST AT E" },
      { kind: "kill", target: "mech", count: 1, text: "CLEAR THE MECH THAT REPOLISHES THE GLASS" },
    ],
    reward: { scrip: 1640, xp: 3280 },
    wasps: 2,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_suture_rack",
    kind: "gig",
    order: 0,
    title: "SUTURE RACK · BONE MARKET",
    level: "bone_market",
    fixer: "vessel",
    brief: "A SUTURE RACK AT B IS STILL CLOSING FILES THE CLINIC ALREADY RELEASED. HOLD THE RACK WHILE THE THREAD DROPS, BREAK THE POST AT E, AND CLEAR THE WASPS THAT RESTITCH THE AISLE.",
    objectives: [
      { kind: "hold", at: { node: "B" }, radius: 6, seconds: 16, text: "HOLD THE RACK WHILE THE THREAD DROPS", waves: 1 },
      { kind: "destroy", spots: [{ node: "E" }], label: "SUTURE POST", text: "BREAK THE POST AT E" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT RESTITCH THE AISLE" },
    ],
    reward: { scrip: 1660, xp: 3320 },
    wasps: 3,
    mechs: 0,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
  {
    id: "g_drip_tray",
    kind: "gig",
    order: 0,
    title: "DRIP TRAY · COLD VAULT",
    level: "cold_vault",
    fixer: "marrow",
    brief: "THE DRIP TRAY AT D IS STILL METERING THAW THE VAULT NEVER SIGNED. REACH THE TRAY, HOLD IT WHILE THE DRIP STOPS, BREAK THE POST AT E, AND WALK OUT THROUGH B.",
    objectives: [
      reach({ node: "D" }, "REACH THE DRIP TRAY AT D"),
      { kind: "hold", at: { node: "D" }, radius: 6, seconds: 16, text: "HOLD THE TRAY WHILE THE DRIP STOPS", waves: 1 },
      { kind: "destroy", spots: [{ node: "E" }], label: "DRIP POST", text: "BREAK THE POST AT E" },
      reach({ node: "B" }, "OUT THROUGH B"),
    ],
    reward: { scrip: 1680, xp: 3360 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_votive_row",
    kind: "gig",
    order: 0,
    title: "VOTIVE ROW · NEON CHAPEL",
    level: "neon_chapel",
    fixer: "deacon",
    brief: "THE VOTIVE ROW AT D IS STILL BURNING NAMES THE CHAPEL BURIED AFTER THE CHOIR WIRE. HOLD THE ROW WHILE THE FLAMES DROP, BREAK THE POST AT B, AND CLEAR THE MECH THAT RELIGHTS THE GLASS.",
    objectives: [
      { kind: "hold", at: { node: "D" }, radius: 6, seconds: 16, text: "HOLD THE ROW WHILE THE FLAMES DROP", waves: 1 },
      { kind: "destroy", spots: [{ node: "B" }], label: "VOTIVE POST", text: "BREAK THE POST AT B" },
      { kind: "kill", target: "mech", count: 1, text: "CLEAR THE MECH THAT RELIGHTS THE GLASS" },
    ],
    reward: { scrip: 1700, xp: 3400 },
    wasps: 2,
    mechs: 1,
  },
  {
    id: "g_crucible_set",
    kind: "gig",
    order: 0,
    title: "CRUCIBLE SET · SLAG PIT",
    level: "slag_pit",
    fixer: "vessel",
    brief: "THE CRUCIBLE AT E IS STILL POURING A HEAT THE PIT CLOSED AFTER THE LADLE SKIM. HOLD THE CRUCIBLE WHILE IT SETS, DISABLE THE MECH ON THE LIP, AND CLEAR THE WASPS THAT REOPEN THE POUR.",
    objectives: [
      { kind: "hold", at: { node: "E" }, radius: 6, seconds: 16, text: "HOLD THE CRUCIBLE WHILE IT SETS", waves: 1 },
      { kind: "kill", target: "mech", count: 1, text: "DISABLE THE MECH ON THE LIP" },
      { kind: "kill", target: "wasp", count: 2, text: "CLEAR THE WASPS THAT REOPEN THE POUR" },
    ],
    reward: { scrip: 1720, xp: 3440 },
    wasps: 3,
    mechs: 1,
    requires: { gate: { not: { "m4:vessel": "expose" } } },
  },
];

export const missionById = (id: string): MissionDef | undefined => MISSIONS.find((m) => m.id === id);
export const MAIN_ARC: readonly MissionDef[] = MISSIONS.filter((m) => m.kind === "mission").sort((a, b) => a.order - b.order);
export const GIGS: readonly MissionDef[] = MISSIONS.filter((m) => m.kind === "gig");
